/**
 * Internationalization (i18n) Module
 * Supports Thai, English, and Chinese
 */

import type { Language } from './config';
export type { Language };

// Translation keys structure
export interface Translations {
  // Common
  appName: string;
  loading: string;
  error: string;
  retry: string;
  back: string;
  next: string;
  confirm: string;
  cancel: string;
  close: string;
  done: string;
  selectLanguage: string;
  backToTop: string;

  // Search page
  searchTitle: string;
  checkIn: string;
  checkOut: string;
  adults: string;
  children: string;
  rooms: string;
  searchButton: string;
  viewAvailabilityCalendar: string;
  availabilityCalendar: string;
  available: string;
  notAvailable: string;
  allRooms: string;
  loadingCalendar: string;
  prevMonth: string;
  nextMonth: string;

  // Room selection
  roomsTitle: string;
  perNight: string;
  totalFor: string;
  nights: string;
  inclTaxService: string;
  selectRoom: string;
  noRoomsAvailable: string;
  changeSearch: string;
  amenities: string;
  cancellationPolicy: string;
  mealPlan: string;

  // Guest info
  guestInfoTitle: string;
  spaGuestInfoTitle: string;
  diningGuestInfoTitle: string;
  registrantInfoTitle: string;
  requesterInfoTitle: string;
  registrantName: string;
  requesterName: string;
  seminarCourse: string;
  speaker: string;
  cpdHours: string;
  confirmRegistration: string;
  registrationSuccess: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  specialRequests: string;
  // Additional guest fields
  guestTitle: string;
  titleMr: string;
  titleMrs: string;
  titleMs: string;
  titleMiss: string;
  titleDr: string;
  selectTitle: string;
  nationality: string;
  nationalityPlaceholder: string;
  idType: string;
  idTypePassport: string;
  idTypeNationalId: string;
  idTypeDrivingLicense: string;
  idNumber: string;
  idNumberPlaceholder: string;
  selectIdType: string;

  // Confirmation
  confirmTitle: string;
  bookingSummary: string;
  roomType: string;
  guestName: string;
  totalAmount: string;
  confirmBooking: string;
  spaDetailsTitle: string;
  spaTreatment: string;
  spaOutlet: string;
  spaRate: string;
  diningDetailsTitle: string;
  diningOutlet: string;
  seatingArea: string;
  serviceDate: string;
  timeSlot: string;
  partySize: string;
  guestsCount: string;
  minutesUnit: string;
  personUnit: string;
  roomPrefix: string;
  roomNumber: string;
  freeReservation: string;

  // Success
  successTitle: string;
  bookingConfirmed: string;
  confirmationNumber: string;
  checkInTime: string;
  checkOutTime: string;
  viewInChat: string;
  confirmationSentToChat: string;
  returnToChatWeb: string;
  returnToWhatsAppChat: string;
  returnToWhatsAppBtn: string;
  returnToWeChatChat: string;
  returnToWeChatBtn: string;
  closeWindowBtn: string;
  returnToServicesBtn: string;

  // Errors
  errorGeneric: string;
  errorNetwork: string;
  errorNoRooms: string;
  errorBookingFailed: string;

  // Validation
  required: string;
  invalidEmail: string;
  invalidPhone: string;
  selectDates: string;

  // Navigation
  home: string;
  stepSearch: string;
  stepRoom: string;
  stepGuest: string;
  stepConfirm: string;

  // Payment
  payAtHotel: string;

  // My Bookings
  myBookings: string;
  bookings: string;
  noBookings: string;
  noBookingsDesc: string;
  startBooking: string;
  statusConfirmed: string;
  statusCancelled: string;
  statusCompleted: string;
  statusPending: string;

  // Extra Charges
  capacityExceeded: string;
  capacityExceededMessage: string;
  extraAdults: string;
  extraChildren: string;
  person: string;
  personPerNight: string;
  totalExtraCharge: string;
  extraCharges: string;
  roomRate: string;
  roomRateBeforeTax: string;
  serviceRateBeforeTax: string;
  serviceCharge10: string;
  vat7: string;

  // Additional details for success page
  ratePlan: string;
  selectRatePlan: string;
  hideRates: string;
  guests: string;
  refundPolicy: string;
  nonRefundablePolicy: string;
  refundablePolicy: string;

  // Auto-fill
  autoFilledFromPrevious: string;
  editingBooking: string;
  confirmEditBooking: string;
  confirmCreateBooking: string;

  // Room Types Browse Page
  roomTypesTitle: string;
  startingFrom: string;
  checkAvailability: string;
  ourRooms: string;
  excludingTaxes: string;

  // Payment
  paymentTitle: string;
  payWithCard: string;
  payWithPromptPay: string;
  processingPayment: string;
  paymentSuccessful: string;
  paymentFailed: string;
  paymentError: string;
  retryPayment: string;
  skipPayment: string;
  payNow: string;
  paymentWarningBanner: string;
  paymentNoGoBack: string;
  paymentBackWarning: string;
  statusPaid: string;

  // Booking Management
  editBooking: string;
  editModeBanner: string;
  cancelEdit: string;
  cancelBooking: string;
  cannotModifyBooking: string;
  saveChanges: string;
  confirmCancelBooking: string;
  keepBooking: string;
  confirmCancel: string;
  bookingUpdatedSuccess: string;
  bookingCancelledSuccess: string;
  errorLoadingBooking: string;
  errorUpdatingBooking: string;
  errorCancellingBooking: string;
  pleaseFillAllFields: string;
  specialRequestsPlaceholder: string;
  cancellationPolicy3Days: string;
  bookingWillBeCancelled: string;
  actionCannotBeUndone: string;
  saving: string;
  checkOutMustBeAfterCheckIn: string;
  cannotModifyLessThan3Days: string;
  roomNotAvailable: string;
  bookingNotFound: string;
  connectionError: string;
  nonRefundable: string;
  nonRefundableWarning: string;
  nonRefundableCannotModify: string;
  paidBookingCannotModify: string;

  // Room Service & Maintenance
  roomServiceTitle: string;
  roomServiceSubtitle: string;
  tabHousekeeping: string;
  tabMaintenance: string;
  tabMyRequests: string;
  roomNumberLabel: string;
  roomNumberPlaceholder: string;
  guestNameLabel: string;
  guestPhoneLabel: string;
  selectItemsTitle: string;
  customNotesLabel: string;
  customNotesPlaceholder: string;
  submitHousekeepingBtn: string;
  selectIssueTitle: string;
  issueDescriptionLabel: string;
  issueDescriptionPlaceholder: string;
  submitMaintenanceBtn: string;
  requestSuccessTitle: string;
  requestSuccessDesc: string;
  noActiveRequests: string;
  statusReqPending: string;
  statusReqInProgress: string;
  statusReqCompleted: string;
  requestReceivedBanner: string;
  newRequestBtn: string;
  roomNumberRequired: string;
  selectAtLeastOneItem: string;
  describeIssueRequired: string;
  latestRequestsHeader: string;
  refreshStatusBtn: string;
  waitingStaffAccept: string;
  teamHousekeeping: string;
  teamMaintenance: string;
  cancelRequestBtn: string;
  tapQtyHint: string;
  submittingRequest: string;
  submittingMaintenance: string;
  confirmCancelRequest: string;
  cannotCancelRequest: string;
  errorCancelRequest: string;
  submitRequestFailed: string;
  submitMaintenanceFailed: string;
  maxItemLimit: string;
  maxLimitReached: string;

  // Restaurant Menu & Ordering
  menuOrderMode: string;
  menuReserveMode: string;
  filterVeg: string;
  filterSpicy: string;
  filterGf: string;
  addToCart: string;
  viewCart: string;
  cartEmpty: string;
  deliverToRoom: string;
  dineIn: string;
  chargeToRoom: string;
  payAtTable: string;
  orderSubtotal: string;
  orderServiceCharge: string;
  orderGrandTotal: string;
  placeOrder: string;
  orderReceived: string;
  orderEta: string;
  soldOut: string;
  yourOrder: string;
  orderDeliveryOption: string;
  orderPaymentMethod: string;
  addMoreItems: string;
  menuLoading: string;
  menuEmptyTitle: string;
  menuEmptySubtitle: string;
  orderHistoryTitle: string;
  orderMoreBtn: string;
  viewOrderHistoryBtn: string;
  roomNumberInput: string;
  tableNumberInput: string;
  tableNumberRequired: string;
  statusOrderReceived: string;
  statusOrderPreparing: string;
  statusOrderReady: string;
  statusOrderOnTheWay: string;
  statusOrderDelivered: string;
  noOrderHistory: string;
  orderTotal: string;
  refreshBtn: string;
  roomBadge: string;
  tableBadge: string;
  activeOrderTracking: string;
  activeOrderPreparing: string;
  activeOrderReady: string;
  activeOrderOnTheWay: string;

  // Generic Multi-Industry Services
  serviceUnavailable: string;
  selectProvider: string;
  selectLocation: string;
  selectTimeSlot: string;
  noSlots: string;
  requestSubmitted: string;
  quotationPending: string;
  comingSoon: string;
  serviceComingSoonDesc: string;
  termItem: string;
  termProvider: string;
  termLocation: string;
  termCustomer: string;

  // Onboarding Free Tier Application
  onboardTitle: string;
  onboardSubtitle: string;
  onboardQuotaActiveBadge: string;
  onboardQuotaFullBadge: string;
  onboardHotelNameLabel: string;
  onboardHotelNamePlaceholder: string;
  onboardTenantCodeLabel: string;
  onboardTenantCodePlaceholder: string;
  onboardContactPersonLabel: string;
  onboardContactPersonPlaceholder: string;
  onboardContactPhoneLabel: string;
  onboardContactPhonePlaceholder: string;
  onboardContactEmailLabel: string;
  onboardContactEmailPlaceholder: string;
  onboardRoomCountLabel: string;
  onboardRoomCountHelp: string;
  onboardProvinceLabel: string;
  onboardProvincePlaceholder: string;
  onboardSelectProvincePlaceholder: string;
  onboardProvinceOther: string;
  onboardSubmitButton: string;
  onboardSubmitting: string;
  onboardSuccessTitle: string;
  onboardPendingTitle: string;
  onboardPendingNotice: string;
  onboardWaitingTitle: string;
  onboardQueueNumberLabel: string;
  onboardFeatureStayTitle: string;
  onboardFeatureDiningTitle: string;
  onboardFeatureSpaTitle: string;
  onboardFeatureMiceTitle: string;
  onboardFeatureAiTitle: string;
  onboardClosingWindow: string;
  onboardCredentialsSentNotice: string;
  onboardCloseNowButton: string;
  onboardSectionHotel: string;
  onboardSectionAdmin: string;
  onboardRequiredFieldsError: string;
  onboardMaxRoomsError: string;
  onboardTermsLabel: string;
  onboardTermsReadLink: string;
  onboardTermsModalTitle: string;
  onboardTermsModalClose: string;
  onboardTermsModalAccept: string;
  onboardTermsRequiredError: string;
  onboardTermsSummaryScope: string;
  onboardTermsSummaryInactivity: string;
  onboardTermsSummaryNoFee: string;
  onboardTermsSummaryDataPrivacy: string;
  onboardTermsSummaryAsIs: string;
}


// Thai translations
const th: Translations = {
  appName: 'จองห้องพัก',
  loading: 'กำลังโหลด...',
  error: 'เกิดข้อผิดพลาด',
  retry: 'ลองอีกครั้ง',
  back: 'ย้อนกลับ',
  next: 'ถัดไป',
  confirm: 'ยืนยัน',
  cancel: 'ยกเลิก',
  close: 'ปิด',
  done: 'เสร็จสิ้น',
  selectLanguage: 'เลือกภาษา',
  backToTop: 'กลับขึ้นด้านบน',

  searchTitle: 'ค้นหาและจอง',
  checkIn: 'วันเช็คอิน',
  checkOut: 'วันเช็คเอาท์',
  adults: 'ผู้ใหญ่',
  children: 'เด็ก',
  rooms: 'ห้อง',
  searchButton: 'ค้นหาและจอง',
  viewAvailabilityCalendar: 'ดูปฏิทินห้องว่าง',
  availabilityCalendar: 'ปฏิทินห้องว่าง',
  available: 'ว่าง',
  notAvailable: 'ห้องเต็ม',
  allRooms: 'ทุกห้อง',
  loadingCalendar: 'กำลังโหลดข้อมูลปฏิทิน...',
  prevMonth: '◀ ก่อนหน้า',
  nextMonth: 'ถัดไป ▶',

  roomsTitle: 'เลือกห้องพัก',
  perNight: '/คืน',
  totalFor: 'รวม',
  inclTaxService: '(รวมภาษี + ค่าบริการ)',
  nights: 'คืน',
  selectRoom: 'เลือกห้องนี้',
  noRoomsAvailable: 'ไม่มีห้องว่างในช่วงเวลาที่เลือก',
  changeSearch: 'แก้ไข',
  amenities: 'สิ่งอำนวยความสะดวก',
  cancellationPolicy: 'นโยบายการยกเลิก',
  mealPlan: 'มื้ออาหาร',

  guestInfoTitle: 'ข้อมูลผู้เข้าพัก',
  spaGuestInfoTitle: 'ข้อมูลผู้รับบริการ',
  diningGuestInfoTitle: 'ข้อมูลผู้จอง',
  registrantInfoTitle: 'ข้อมูลผู้ลงทะเบียน',
  requesterInfoTitle: 'ข้อมูลผู้ยื่นคำขอ',
  registrantName: 'ชื่อผู้ลงทะเบียน',
  requesterName: 'ชื่อผู้ยื่นคำขอ',
  seminarCourse: 'หลักสูตร / หัวข้ออบรม',
  speaker: 'วิทยากร',
  cpdHours: 'ชั่วโมง CPD',
  confirmRegistration: 'ยืนยันการลงทะเบียน',
  registrationSuccess: 'ลงทะเบียนสำเร็จ',
  firstName: 'ชื่อ',
  lastName: 'นามสกุล',
  email: 'อีเมล',
  phone: 'เบอร์โทรศัพท์',
  specialRequests: 'คำขอพิเศษ (ถ้ามี)',
  guestTitle: 'คำนำหน้า',
  titleMr: 'นาย',
  titleMrs: 'นาง',
  titleMs: 'น.ส.',
  titleMiss: 'น.ส.',
  titleDr: 'ดร.',
  selectTitle: '-- เลือกคำนำหน้า --',
  nationality: 'สัญชาติ',
  nationalityPlaceholder: 'เช่น ไทย, จีน, อังกฤษ',
  idType: 'ประเภทเอกสารประจำตัว',
  idTypePassport: 'หนังสือเดินทาง',
  idTypeNationalId: 'บัตรประชาชน',
  idTypeDrivingLicense: 'ใบขับขี่',
  idNumber: 'เลขที่เอกสาร',
  idNumberPlaceholder: 'กรุณากรอกเลขที่เอกสาร',
  selectIdType: '-- เลือกประเภทเอกสาร --',

  confirmTitle: 'ยืนยันการจอง',
  bookingSummary: 'สรุปการจอง',
  roomType: 'ประเภทห้อง',
  guestName: 'ชื่อผู้เข้าพัก',
  totalAmount: 'ยอดรวมทั้งหมด',
  confirmBooking: 'ยืนยันการจอง',
  spaDetailsTitle: 'รายละเอียดสปา',
  spaTreatment: 'ทรีตเมนต์',
  spaOutlet: 'สปา',
  spaRate: 'ค่าบริการทรีตเมนต์',
  diningDetailsTitle: 'รายละเอียดการจองโต๊ะอาหาร',
  diningOutlet: 'ห้องอาหาร',
  seatingArea: 'โซนที่นั่ง',
  serviceDate: 'วันที่รับบริการ',
  timeSlot: 'รอบเวลา',
  partySize: 'จำนวนที่นั่ง',
  guestsCount: 'จำนวนผู้รับบริการ',
  minutesUnit: 'นาที',
  personUnit: 'ท่าน',
  roomPrefix: 'ห้อง',
  roomNumber: 'หมายเลขห้องพัก',
  freeReservation: 'สำรองที่นั่งฟรี',

  successTitle: 'จองสำเร็จ!',
  bookingConfirmed: 'การจองของคุณได้รับการยืนยันแล้ว',
  confirmationNumber: 'หมายเลขยืนยัน',
  checkInTime: 'เวลาเช็คอิน: 14:00 น.',
  checkOutTime: 'เวลาเช็คเอาท์: 12:00 น.',
  viewInChat: 'ดูในแชท LINE',
  confirmationSentToChat: 'ส่งข้อมูลยืนยันไปยังห้องแชท LINE แล้ว ระบบกำลังนำท่านกลับสู่แชท...',
  returnToChatWeb: 'การจองของคุณได้รับการยืนยันเรียบร้อยแล้ว แตะปุ่มด้านล่างเพื่อปิดหน้านี้และกลับสู่หน้าเว็บแชทครับ',
  returnToWhatsAppChat: 'การจองของคุณได้รับการยืนยันเรียบร้อยแล้ว แตะปุ่มด้านล่างเพื่อกลับสู่ห้องแชทครับ',
  returnToWhatsAppBtn: 'กลับสู่ WhatsApp แชท',
  returnToWeChatChat: 'การจองของคุณได้รับการยืนยันเรียบร้อยแล้ว แตะปุ่มด้านล่างเพื่อปิดหน้านี้และกลับสู่ WeChat ครับ',
  returnToWeChatBtn: 'ปิดหน้านี้เพื่อกลับสู่ WeChat',
  closeWindowBtn: 'ปิดหน้านี้',
  returnToServicesBtn: 'กลับสู่หน้าหลัก / บริการ',

  errorGeneric: 'เกิดข้อผิดพลาด กรุณาลองใหม่อีกครั้ง',
  errorNetwork: 'ไม่สามารถเชื่อมต่อได้ กรุณาตรวจสอบอินเทอร์เน็ต',
  errorNoRooms: 'ไม่พบห้องว่างในช่วงเวลาที่เลือก',
  errorBookingFailed: 'ไม่สามารถจองได้ กรุณาลองใหม่',

  required: 'กรุณากรอกข้อมูลนี้',
  invalidEmail: 'อีเมลไม่ถูกต้อง',
  invalidPhone: 'เบอร์โทรศัพท์ไม่ถูกต้อง',
  selectDates: 'กรุณาเลือกวันที่',

  home: 'หน้าแรก',
  stepSearch: 'ค้นหา',
  stepRoom: 'เลือกห้อง',
  stepGuest: 'ข้อมูล',
  stepConfirm: 'ยืนยัน',

  payAtHotel: 'ชำระเงินที่โรงแรม',

  // My Bookings
  myBookings: 'การจองของฉัน',
  bookings: 'รายการ',
  noBookings: 'ยังไม่มีการจอง',
  noBookingsDesc: 'คุณยังไม่มีประวัติการทำรายการหรือการจอง',
  startBooking: 'เริ่มต้นการจอง',
  statusConfirmed: 'ยืนยันแล้ว',
  statusCancelled: 'ยกเลิกแล้ว',
  statusCompleted: 'เข้าพักแล้ว',
  statusPending: 'รอดำเนินการ',

  capacityExceeded: 'จำนวนผู้เข้าพักเกินกำหนด',
  capacityExceededMessage:
    'จำนวนผู้เข้าพักของคุณเกินขีดจำกัดของห้องพัก คุณต้องการชำระค่าบริการเพิ่มหรือไม่?',
  extraAdults: 'ผู้ใหญ่เพิ่ม',
  extraChildren: 'เด็กเพิ่ม',
  person: 'คน',
  personPerNight: 'คน/คืน',
  totalExtraCharge: 'ค่าบริการเพิ่มทั้งหมด',
  extraCharges: 'ค่าบริการเพิ่ม',
  roomRate: 'ค่าห้องพัก',
  roomRateBeforeTax: 'ค่าห้อง (ก่อนภาษี)',
  serviceRateBeforeTax: 'ค่าบริการ (ก่อนภาษี)',
  serviceCharge10: 'ค่าบริการ 10%',
  vat7: 'VAT 7%',
  ratePlan: 'แผนราคา',
  selectRatePlan: 'เลือกแพ็กเกจราคา',
  hideRates: 'ซ่อนราคา',
  guests: 'ผู้เข้าพัก',
  refundPolicy: 'นโยบายการคืนเงิน',
  nonRefundablePolicy: 'ไม่สามารถคืนเงินได้',
  refundablePolicy: 'ยกเลิกฟรีตามเงื่อนไข',
  autoFilledFromPrevious: 'ข้อมูลถูกกรอกอัตโนมัติจากการจองครั้งก่อน',
  editingBooking: 'กำลังแก้ไขการจอง',
  confirmEditBooking: 'ยืนยันการแก้ไขการจองนี้?',
  confirmCreateBooking: 'ยืนยันการจองนี้?',

  // Room Types Browse Page
  roomTypesTitle: 'ประเภทห้องพัก',
  startingFrom: 'เริ่มต้น',
  checkAvailability: 'ตรวจสอบห้องว่าง',
  ourRooms: 'ห้องพักของเรา',
  excludingTaxes: 'ยังไม่รวมภาษีและค่าบริการ',

  // Payment
  paymentTitle: 'ชำระเงิน',
  payWithCard: 'ชำระด้วยบัตรเครดิต/เดบิต',
  payWithPromptPay: 'ชำระด้วย PromptPay',
  processingPayment: 'กำลังประมวลผลการชำระเงิน...',
  paymentSuccessful: 'ชำระเงินสำเร็จ!',
  paymentFailed: 'การชำระเงินล้มเหลว',
  paymentError: 'เกิดข้อผิดพลาดในการชำระเงิน กรุณาลองใหม่',
  retryPayment: 'ลองชำระเงินอีกครั้ง',
  skipPayment: 'ชำระเงินที่โรงแรม',
  payNow: 'ชำระเงินเลย',
  paymentWarningBanner: 'การจองของคุณได้รับการยืนยันแล้ว กรุณาชำระเงินหรือเลือก "ชำระที่โรงแรม"',
  paymentNoGoBack: 'ไม่สามารถย้อนกลับได้',
  paymentBackWarning: 'การจองได้รับการยืนยันแล้ว\nกรุณาชำระเงินหรือเลือก\n"ชำระที่โรงแรม"',
  statusPaid: 'ชำระออนไลน์แล้ว',

  // Booking Management
  editBooking: 'แก้ไขการจอง',
  editModeBanner: 'โหมดแก้ไขการจอง',
  cancelEdit: 'ยกเลิกการแก้ไข',
  cancelBooking: 'ยกเลิกการจอง',
  cannotModifyBooking:
    'ไม่สามารถแก้ไขหรือยกเลิกได้ (เหลือเวลาน้อยกว่า 3 วัน) กรุณาติดต่อโรงแรมโดยตรง',
  saveChanges: 'บันทึกการเปลี่ยนแปลง',
  confirmCancelBooking: 'คุณแน่ใจหรือไม่ว่าต้องการยกเลิกการจองนี้?',
  keepBooking: 'เก็บการจอง',
  confirmCancel: 'ยืนยันยกเลิก',
  bookingUpdatedSuccess: 'อัปเดตการจองสำเร็จ',
  bookingCancelledSuccess: 'ยกเลิกการจองสำเร็จ',
  errorLoadingBooking: 'ไม่สามารถโหลดข้อมูลการจองได้',
  errorUpdatingBooking: 'ไม่สามารถอัปเดตการจองได้',
  errorCancellingBooking: 'ไม่สามารถยกเลิกการจองได้',
  pleaseFillAllFields: 'กรุณากรอกข้อมูลให้ครบถ้วน',
  specialRequestsPlaceholder: 'เช่น ต้องการห้องชั้นสูง, เตียงเสริม, ฯลฯ',
  cancellationPolicy3Days: 'ต้องยกเลิกก่อนวันเช็คอินอย่างน้อย 3 วัน',
  bookingWillBeCancelled: 'การจองจะถูกยกเลิกและไม่สามารถกู้คืนได้',
  actionCannotBeUndone: 'การดำเนินการนี้ไม่สามารถย้อนกลับได้!',
  saving: 'กำลังบันทึก',
  checkOutMustBeAfterCheckIn: 'วันเช็คเอาท์ต้องหลังจากวันเช็คอิน',
  cannotModifyLessThan3Days:
    'ไม่สามารถแก้ไขได้ เนื่องจากเหลือเวลาน้อยกว่า 3 วัน กรุณาติดต่อโรงแรมโดยตรง',
  roomNotAvailable: 'ห้องไม่ว่างในวันที่เลือก กรุณาเลือกวันอื่น',
  bookingNotFound: 'ไม่พบข้อมูลการจอง',
  connectionError: 'เกิดข้อผิดพลาดในการเชื่อมต่อ กรุณาตรวจสอบอินเทอร์เน็ตและลองใหม่อีกครั้ง',
  nonRefundable: 'นโยบายการคืนเงิน: ไม่สามารถคืนเงินได้',
  nonRefundableWarning: 'ราคานี้เป็นแบบ Non-Refundable ไม่สามารถยกเลิกหรือขอคืนเงินได้ และต้องชำระออนไลน์เท่านั้น',
  nonRefundableCannotModify: 'ราคา Non-Refundable ไม่สามารถแก้ไขหรือยกเลิกได้ กรุณาติดต่อโรงแรมโดยตรง',
  paidBookingCannotModify:
    'การจองนี้ชำระเงินแล้ว หากต้องการแก้ไขหรือยกเลิก กรุณาติดต่อโรงแรมโดยตรง',

  // Room Service & Maintenance
  roomServiceTitle: 'บริการห้องพัก & แจ้งซ่อม',
  roomServiceSubtitle: 'ขอของใช้จากแม่บ้าน หรือแจ้งซ่อมอุปกรณ์ในห้องพัก',
  tabHousekeeping: '🧹 ขอของใช้แม่บ้าน',
  tabMaintenance: '🛠️ แจ้งซ่อมบำรุง',
  tabMyRequests: '📋 รายการคำขอของฉัน',
  roomNumberLabel: 'หมายเลขห้องพัก',
  roomNumberPlaceholder: 'เช่น 302 หรือ Villa 5',
  guestNameLabel: 'ชื่อผู้เข้าพัก',
  guestPhoneLabel: 'เบอร์ติดต่อ',
  selectItemsTitle: 'เลือกของใช้ที่ต้องการ (แตะเพื่อเลือก)',
  customNotesLabel: 'หมายเหตุ / ระบุเพิ่มเติม',
  customNotesPlaceholder: 'เช่น ขอหมอนขนนก, ขอผ้าห่มอุ่นๆ, ส่งช่วง 18:00 น.',
  submitHousekeepingBtn: 'ส่งคำขอถึงแม่บ้าน',
  selectIssueTitle: 'เลือกหมวดปัญหา / สิ่งชำรุด (แตะเพื่อเลือก)',
  issueDescriptionLabel: 'รายละเอียดอาการเสีย / จุดที่ชำรุด',
  issueDescriptionPlaceholder: 'เช่น แอร์มีน้ำหยด, ฝักบัวน้ำไม่ร้อน, หลอดไฟห้องน้ำดับ',
  submitMaintenanceBtn: 'ส่งเรื่องแจ้งซ่อม',
  requestSuccessTitle: 'ส่งคำขอเรียบร้อยแล้ว!',
  requestSuccessDesc: 'เจ้าหน้าที่ได้รับเรื่องแล้วและกำลังดำเนินการให้ท่านโดยเร็วที่สุด',
  noActiveRequests: 'ยังไม่มีประวัติคำขอบริการห้องพัก',
  statusReqPending: 'รอดำเนินการ',
  statusReqInProgress: 'กำลังดำเนินการ',
  statusReqCompleted: 'เสร็จสมบูรณ์',
  requestReceivedBanner: 'เจ้าหน้าที่รับเรื่องเรียบร้อยแล้ว',
  newRequestBtn: '+ ส่งคำขอใหม่',
  roomNumberRequired: 'กรุณากรอกหมายเลขห้องพัก',
  selectAtLeastOneItem: 'กรุณาเลือกของใช้ที่ต้องการอย่างน้อย 1 รายการ หรือระบุในช่องหมายเหตุ',
  describeIssueRequired: 'กรุณาเลือกหมวดปัญหาหรือระบุอาการที่ชำรุด',
  latestRequestsHeader: 'รายการคำขอล่าสุด',
  refreshStatusBtn: '🔄 อัปเดตสถานะ',
  waitingStaffAccept: 'รอเจ้าหน้าที่รับงาน',
  teamHousekeeping: 'ทีมแม่บ้าน',
  teamMaintenance: 'ทีมช่างซ่อมบำรุง',
  cancelRequestBtn: '🗑️ ยกเลิก',
  tapQtyHint: '(แตะปุ่ม +/- เพื่อเลือกจำนวน)',
  submittingRequest: 'กำลังส่งคำขอ...',
  submittingMaintenance: 'กำลังส่งแจ้งซ่อม...',
  confirmCancelRequest: 'ต้องการยกเลิกคำขอนี้ใช่หรือไม่?',
  cannotCancelRequest: 'ไม่สามารถยกเลิกคำขอได้',
  errorCancelRequest: 'เกิดข้อผิดพลาดในการยกเลิกคำขอ: ',
  submitRequestFailed: 'ส่งคำขอล้มเหลว กรุณาลองใหม่อีกครั้ง',
  submitMaintenanceFailed: 'ส่งแจ้งซ่อมล้มเหลว กรุณาลองใหม่อีกครั้ง',
  maxItemLimit: 'ขอได้สูงสุด {max} ชิ้น',
  maxLimitReached: 'ขอได้สูงสุดไม่เกิน {max} ชิ้นต่อครั้ง',
  // Restaurant Menu & Ordering
  menuOrderMode: '📋 สั่งอาหาร',
  menuReserveMode: '🪑 จองโต๊ะ',
  filterVeg: '🌿 มังสวิรัติ',
  filterSpicy: '🌶 เผ็ด',
  filterGf: '🌾 ไม่มีกลูเตน',
  addToCart: 'เพิ่ม',
  viewCart: 'ดูออเดอร์',
  cartEmpty: 'ตะกร้าว่าง',
  deliverToRoom: '🛏️ ส่งมาที่ห้อง',
  dineIn: '🍽️ ทานที่ร้าน',
  chargeToRoom: '🧾 เก็บเข้าบิลห้อง',
  payAtTable: '💳 ชำระที่โต๊ะ',
  orderSubtotal: 'ยอดรวม',
  orderServiceCharge: 'ค่าบริการ (10%)',
  orderGrandTotal: 'ยอดรวมทั้งหมด',
  placeOrder: 'ยืนยันการสั่งอาหาร',
  orderReceived: '✅ รับออเดอร์แล้ว!',
  orderEta: '⏱ เวลาโดยประมาณ ~25–35 นาที',
  soldOut: 'หมดแล้ว',
  yourOrder: 'ออเดอร์ของคุณ',
  orderDeliveryOption: 'รูปแบบการรับอาหาร',
  orderPaymentMethod: 'วิธีชำระเงิน',
  addMoreItems: 'สั่งเพิ่ม',
  menuLoading: 'กำลังโหลดเมนูอาหาร...',
  menuEmptyTitle: 'ยังไม่มีรายการเมนู',
  menuEmptySubtitle: 'ร้านนี้ยังไม่ได้เพิ่มรายการเมนูในระบบ',
  orderHistoryTitle: 'ประวัติการสั่งอาหาร',
  orderMoreBtn: '🛒 สั่งอาหารเพิ่ม',
  viewOrderHistoryBtn: '📋 ดูสถานะและประวัติออเดอร์',
  roomNumberInput: 'หมายเลขห้องพัก (เช่น 204)',
  tableNumberInput: 'หมายเลขโต๊ะ (เช่น T-05)',
  tableNumberRequired: 'กรุณาระบุหมายเลขโต๊ะ',
  statusOrderReceived: 'ได้รับออเดอร์แล้ว',
  statusOrderPreparing: 'กำลังเตรียมอาหาร',
  statusOrderReady: 'อาหารพร้อมเสิร์ฟ',
  statusOrderOnTheWay: 'กำลังจัดส่ง',
  statusOrderDelivered: 'จัดส่งเรียบร้อย',
  noOrderHistory: 'ยังไม่มีประวัติการสั่งอาหาร',
  orderTotal: 'รวม',
  refreshBtn: 'รีเฟรช',
  roomBadge: 'ห้อง',
  tableBadge: 'โต๊ะ',
  activeOrderTracking: 'ติดตามออเดอร์',
  activeOrderPreparing: 'กำลังเตรียมอาหาร...',
  activeOrderReady: 'อาหารพร้อมเสิร์ฟแล้ว',
  activeOrderOnTheWay: 'กำลังจัดส่ง...',

  // Generic Multi-Industry Services
  serviceUnavailable: 'บริการนี้ยังไม่พร้อมใช้งานในขณะนี้',
  selectProvider: 'เลือกผู้ให้บริการ / ผู้เชี่ยวชาญ',
  selectLocation: 'เลือกสาขา / แผนก',
  selectTimeSlot: 'เลือกรอบเวลา',
  noSlots: 'ไม่มีรอบเวลาว่างในวันที่เลือก',
  requestSubmitted: 'ส่งคำขอรับบริการเรียบร้อยแล้ว',
  quotationPending: 'ระบบกำลังดำเนินการจัดทำใบเสนอราคา',
  comingSoon: 'เร็วๆ นี้',
  serviceComingSoonDesc: 'บริการนี้กำลังอยู่ระหว่างการจัดเตรียมความพร้อม',
  termItem: 'รายการบริการ',
  termProvider: 'ผู้ให้บริการ',
  termLocation: 'สาขา/สถานที่',
  termCustomer: 'ลูกค้า',

  // Onboarding Free Tier Application
  onboardTitle: 'สมัครใช้งาน Revio Free Tier',
  onboardSubtitle: 'สำหรับโรงแรมและรีสอร์ตขนาดไม่เกิน 25 ห้อง ใช้งานฟรีไม่มีค่าใช้จ่าย',
  onboardQuotaActiveBadge: 'เหลือสิทธิ์ทันที {slots} / {max} โรงแรม',
  onboardQuotaFullBadge: 'โควต้าเต็ม (50/50) — สมัครเข้า Waiting List ได้ทันที',
  onboardHotelNameLabel: 'ชื่อโรงแรม / ที่พัก',
  onboardHotelNamePlaceholder: 'เช่น บ้านสวนริมน้ำ รีสอร์ต',
  onboardTenantCodeLabel: 'รหัสระบบ (ภาษาอังกฤษตัวพิมพ์เล็ก)',
  onboardTenantCodePlaceholder: 'เช่น baansuan-resort',
  onboardContactPersonLabel: 'ชื่อ-นามสกุล ผู้ดูแลระบบ',
  onboardContactPersonPlaceholder: 'เช่น สมชาย ใจดี',
  onboardContactPhoneLabel: 'เบอร์โทรศัพท์ติดต่อ',
  onboardContactPhonePlaceholder: 'เช่น 0812345678',
  onboardContactEmailLabel: 'อีเมลรับข้อมูล',
  onboardContactEmailPlaceholder: 'เช่น hotel@example.com',
  onboardRoomCountLabel: 'จำนวนห้องพักทั้งหมด',
  onboardRoomCountHelp: 'Free Tier จำกัดสูงสุดไม่เกิน 25 ห้อง',
  onboardProvinceLabel: 'จังหวัดที่ตั้ง',
  onboardProvincePlaceholder: 'เช่น เชียงใหม่, ภูเก็ต, กรุงเทพฯ',
  onboardSelectProvincePlaceholder: '-- กรุณาเลือกจังหวัด --',
  onboardProvinceOther: 'อื่นๆ / นอกประเทศไทย',
  onboardSubmitButton: 'ยืนยันการสมัคร Free Tier',
  onboardSubmitting: 'กำลังส่งข้อมูล...',
  onboardSuccessTitle: 'อนุมัติสิทธิ์เรียบร้อยแล้ว!',
  onboardPendingTitle: 'ได้รับใบสมัครแล้ว (รอการอนุมัติ)',
  onboardPendingNotice: 'ระบบได้รับข้อมูลการสมัครของท่านแล้ว ขณะนี้อยู่ระหว่างการตรวจสอบโดยผู้ดูแลระบบ เมื่อได้รับการอนุมัติ รหัสผ่านจะถูกจัดส่งไปยังอีเมลและ LINE ของท่านทันที',
  onboardWaitingTitle: 'บันทึกเข้า Waiting List เรียบร้อยแล้ว',
  onboardQueueNumberLabel: 'คิวลำดับที่',
  onboardFeatureStayTitle: 'ระบบจองห้องพัก PMS (สูงสุด 25 ห้อง)',
  onboardFeatureDiningTitle: 'ห้องอาหาร & รูมเซอร์วิส (10 โต๊ะ, 150 ออเดอร์/เดือน)',
  onboardFeatureSpaTitle: 'สปาและนวด (2 เตียง, 30 จอง/เดือน)',
  onboardFeatureMiceTitle: 'จัดเลี้ยงสัมมนา RFP (15 คำขอ/เดือน)',
  onboardFeatureAiTitle: 'AI Concierge อัจฉริยะ (100 ข้อความ/เดือน)',
  onboardClosingWindow: 'กำลังปิดหน้าต่างและกลับสู่ LINE ใน 1 วินาที...',
  onboardCredentialsSentNotice: 'ข้อมูลบัญชีผู้ดูแลระบบ (Username / Password) ถูกส่งไปที่ LINE และอีเมลเรียบร้อยแล้ว',
  onboardCloseNowButton: 'ปิดหน้าต่างทันที',
  onboardSectionHotel: 'ข้อมูลที่พัก / โรงแรม',
  onboardSectionAdmin: 'ข้อมูลผู้ดูแลระบบ',
  onboardRequiredFieldsError: 'กรุณากรอกข้อมูลที่จำเป็น (*) ให้ครบถ้วน',
  onboardMaxRoomsError: 'Free Tier จำกัดไม่เกิน 25 ห้อง หากต้องการเกินกรุณาเลือก Standard/Enterprise',
  onboardTermsLabel: 'ฉันได้อ่านและยอมรับข้อกำหนดการให้บริการ Free Tier และนโยบายความเป็นส่วนตัวของ Revio',
  onboardTermsReadLink: 'อ่านข้อกำหนดและเงื่อนไขฉบับเต็ม',
  onboardTermsModalTitle: 'ข้อกำหนดและเงื่อนไขการใช้สิทธิ์ Revio Free Tier',
  onboardTermsModalClose: 'ปิด',
  onboardTermsModalAccept: 'ฉันเข้าใจและยอมรับข้อกำหนด',
  onboardTermsRequiredError: 'กรุณากดยอมรับข้อกำหนดและเงื่อนไขก่อนส่งใบสมัคร',
  onboardTermsSummaryScope: 'ขอบเขตสิทธิ์ฟรี: สิทธิ์ใช้งานระบบ Revio สูงสุด 25 ห้อง / 10 โต๊ะ พร้อมฟังก์ชัน AI และการเชื่อมต่อ LINE OA',
  onboardTermsSummaryInactivity: 'เงื่อนไขความต่อเนื่อง: หากไม่มีการใช้งานติดต่อกัน 30 วัน ระบบขอสงวนสิทธิ์ในการส่งต่อสิทธิ์ให้ธุรกิจใน Waiting List',
  onboardTermsSummaryNoFee: 'ไม่มีค่าใช้จ่ายแอบแฝง: สิทธิ์ฟรี 100% ไม่มีการผูกบัตรเครดิตล่วงหน้า และไม่มีการเรียกเก็บเงินย้อนหลัง',
  onboardTermsSummaryDataPrivacy: 'ความเป็นเจ้าของข้อมูล: ข้อมูลลูกค้าและประวัติการจองเป็นกรรมสิทธิ์ของท่านตามกฎหมาย PDPA',
  onboardTermsSummaryAsIs: 'การให้บริการ: ระบบให้บริการในลักษณะ As-Is ไม่มีค่าปรับปรุงหรือการการันตี SLA เหมือนแพ็กเกจระดับองค์กร',
};


// English translations
const en: Translations = {
  appName: 'Book a Room',
  loading: 'Loading...',
  error: 'Error',
  retry: 'Try Again',
  back: 'Back',
  next: 'Next',
  confirm: 'Confirm',
  cancel: 'Cancel',
  close: 'Close',
  done: 'Done',
  selectLanguage: 'Select Language',
  backToTop: 'BACK TO TOP',

  searchTitle: 'Search & Book',
  checkIn: 'Check-in',
  checkOut: 'Check-out',
  adults: 'Adults',
  children: 'Children',
  rooms: 'Rooms',
  searchButton: 'Search & Book',
  viewAvailabilityCalendar: 'VIEW AVAILABILITY CALENDAR',
  availabilityCalendar: 'AVAILABILITY CALENDAR',
  available: 'Available',
  notAvailable: 'Not available',
  allRooms: 'All rooms',
  loadingCalendar: 'Loading calendar...',
  prevMonth: '◀ Prev',
  nextMonth: 'Next ▶',

  roomsTitle: 'Select a Room',
  perNight: '/night',
  totalFor: 'Total for',
  inclTaxService: '(incl. taxes & service)',
  nights: 'nights',
  selectRoom: 'Select This Room',
  noRoomsAvailable: 'No rooms available for selected dates',
  changeSearch: 'Edit',
  amenities: 'Amenities',
  cancellationPolicy: 'Cancellation Policy',
  mealPlan: 'Meal Plan',

  guestInfoTitle: 'Guest Information',
  spaGuestInfoTitle: 'Guest Details',
  diningGuestInfoTitle: 'Contact Details',
  registrantInfoTitle: 'Registrant Information',
  requesterInfoTitle: 'Requester Information',
  registrantName: 'Registrant Name',
  requesterName: 'Requester Name',
  seminarCourse: 'Course / Seminar Topic',
  speaker: 'Speaker / Lecturer',
  cpdHours: 'CPD Hours',
  confirmRegistration: 'Confirm Registration',
  registrationSuccess: 'Registration Confirmed!',
  firstName: 'First Name',
  lastName: 'Last Name',
  email: 'Email',
  phone: 'Phone Number',
  specialRequests: 'Special Requests (optional)',
  guestTitle: 'Title',
  titleMr: 'Mr.',
  titleMrs: 'Mrs.',
  titleMs: 'Ms.',
  titleMiss: 'Miss',
  titleDr: 'Dr.',
  selectTitle: '-- Select Title --',
  nationality: 'Nationality',
  nationalityPlaceholder: 'e.g. Thai, Chinese, British',
  idType: 'ID Document Type',
  idTypePassport: 'Passport',
  idTypeNationalId: 'National ID',
  idTypeDrivingLicense: 'Driving License',
  idNumber: 'ID Document Number',
  idNumberPlaceholder: 'Enter document number',
  selectIdType: '-- Select document type --',

  confirmTitle: 'Confirm Booking',
  bookingSummary: 'Booking Summary',
  roomType: 'Room Type',
  guestName: 'Guest Name',
  totalAmount: 'Total Amount',
  confirmBooking: 'Confirm Booking',
  spaDetailsTitle: 'Spa Details',
  spaTreatment: 'Treatment',
  spaOutlet: 'Spa',
  spaRate: 'Treatment Rate',
  diningDetailsTitle: 'Dining Details',
  diningOutlet: 'Restaurant',
  seatingArea: 'Seating Area',
  serviceDate: 'Service Date',
  timeSlot: 'Time Slot',
  partySize: 'Party Size',
  guestsCount: 'Guests',
  minutesUnit: 'mins',
  personUnit: 'Guests',
  roomPrefix: 'Room',
  roomNumber: 'Room Number',
  freeReservation: 'Free Reservation',

  successTitle: 'Booking Confirmed!',
  bookingConfirmed: 'Your booking has been confirmed',
  confirmationNumber: 'Confirmation Number',
  checkInTime: 'Check-in Time: 2:00 PM',
  checkOutTime: 'Check-out Time: 12:00 PM',
  viewInChat: 'View in LINE Chat',
  confirmationSentToChat: 'Confirmation sent to LINE chat. Redirecting you back to chat...',
  returnToChatWeb: 'Your booking has been confirmed. Tap below to close this window and return to the chat.',
  returnToWhatsAppChat: 'Your booking has been confirmed. Tap below to return to the chat.',
  returnToWhatsAppBtn: 'Return to WhatsApp',
  returnToWeChatChat: 'Your booking has been confirmed. Tap below to close this window and return to WeChat.',
  returnToWeChatBtn: 'Close & Return to WeChat',
  closeWindowBtn: 'Close Window',
  returnToServicesBtn: 'Back to Services / Home',

  errorGeneric: 'An error occurred. Please try again.',
  errorNetwork: 'Connection failed. Please check your internet.',
  errorNoRooms: 'No rooms found for selected dates.',
  errorBookingFailed: 'Booking failed. Please try again.',

  required: 'This field is required',
  invalidEmail: 'Invalid email address',
  invalidPhone: 'Invalid phone number',
  selectDates: 'Please select dates',

  home: 'Home',
  stepSearch: 'Search',
  stepRoom: 'Room',
  stepGuest: 'Guest',
  stepConfirm: 'Confirm',

  payAtHotel: 'Pay at Hotel',

  // My Bookings
  myBookings: 'My Bookings',
  bookings: 'booking(s)',
  noBookings: 'No Bookings Yet',
  noBookingsDesc: "You don't have any booking history",
  startBooking: 'Start Booking',
  statusConfirmed: 'Confirmed',
  statusCancelled: 'Cancelled',
  statusCompleted: 'Completed',
  statusPending: 'Pending',

  capacityExceeded: 'Capacity Exceeded',
  capacityExceededMessage:
    'The number of guests exceeds the room capacity. Would you like to proceed with extra charges?',
  extraAdults: 'Extra Adults',
  extraChildren: 'Extra Children',
  person: 'person',
  personPerNight: 'person/night',
  totalExtraCharge: 'Total Extra Charge',
  extraCharges: 'Extra Charges',
  roomRate: 'Room Rate',
  roomRateBeforeTax: 'Room Rate (Before Tax)',
  serviceRateBeforeTax: 'Service Rate (Before Tax)',
  serviceCharge10: 'Service Charge 10%',
  vat7: 'VAT 7%',
  ratePlan: 'Rate Plan',
  selectRatePlan: 'Select Rate Plan',
  hideRates: 'Hide Rates',
  guests: 'Guests',
  refundPolicy: 'Refund Policy',
  nonRefundablePolicy: 'Non-refundable',
  refundablePolicy: 'Free cancellation',
  autoFilledFromPrevious: 'Information auto-filled from your previous booking',
  editingBooking: 'Editing booking',
  confirmEditBooking: 'Confirm booking update?',
  confirmCreateBooking: 'Confirm this booking?',

  // Room Types Browse Page
  roomTypesTitle: 'Room Types',
  startingFrom: 'Starting from',
  checkAvailability: 'Check Availability',
  ourRooms: 'Our Rooms',
  excludingTaxes: 'Excluding taxes & service charge',

  // Payment
  paymentTitle: 'Payment',
  payWithCard: 'Pay with Credit/Debit Card',
  payWithPromptPay: 'Pay with PromptPay',
  processingPayment: 'Processing payment...',
  paymentSuccessful: 'Payment successful!',
  paymentFailed: 'Payment failed',
  paymentError: 'Payment error occurred. Please try again.',
  retryPayment: 'Retry Payment',
  skipPayment: 'Pay at Hotel',
  payNow: 'Pay Now',
  paymentWarningBanner: 'Your booking is confirmed. Please complete payment or choose "Pay at Hotel"',
  paymentNoGoBack: 'Cannot go back',
  paymentBackWarning: 'Booking is confirmed\nPlease pay now or choose\n"Pay at Hotel"',
  statusPaid: 'Paid Online',

  // Booking Management
  editBooking: 'Edit Booking',
  editModeBanner: 'Edit Booking Mode',
  cancelEdit: 'Cancel Edit',
  cancelBooking: 'Cancel Booking',
  cannotModifyBooking:
    'Cannot modify or cancel (less than 3 days remaining). Please contact the hotel directly.',
  saveChanges: 'Save Changes',
  confirmCancelBooking: 'Are you sure you want to cancel this booking?',
  keepBooking: 'Keep Booking',
  confirmCancel: 'Confirm Cancellation',
  bookingUpdatedSuccess: 'Booking updated successfully',
  bookingCancelledSuccess: 'Booking cancelled successfully',
  errorLoadingBooking: 'Failed to load booking details',
  errorUpdatingBooking: 'Failed to update booking',
  errorCancellingBooking: 'Failed to cancel booking',
  pleaseFillAllFields: 'Please fill in all required fields',
  specialRequestsPlaceholder: 'e.g. High floor, extra bed, etc.',
  cancellationPolicy3Days: 'Must cancel at least 3 days before check-in',
  bookingWillBeCancelled: 'Booking will be cancelled and cannot be restored',
  actionCannotBeUndone: 'This action cannot be undone!',
  saving: 'Saving',
  checkOutMustBeAfterCheckIn: 'Check-out date must be after check-in date',
  cannotModifyLessThan3Days:
    'Cannot modify booking with less than 3 days remaining. Please contact the hotel directly',
  roomNotAvailable: 'Room not available for selected dates. Please choose different dates',
  bookingNotFound: 'Booking not found',
  connectionError: 'Connection error occurred. Please check your internet and try again',
  nonRefundable: '⛔ Non-Refundable',
  nonRefundableWarning: 'This rate is Non-Refundable. Cancellations cannot be made and online payment is required.',
  nonRefundableCannotModify: 'Non-Refundable rate cannot be modified or cancelled. Please contact the hotel directly.',
  paidBookingCannotModify:
    'This booking has been paid. To modify or cancel, please contact the hotel directly.',

  // Room Service & Maintenance
  roomServiceTitle: 'Room Care & Maintenance',
  roomServiceSubtitle: 'Request housekeeping items or report room maintenance',
  tabHousekeeping: '🧹 Housekeeping',
  tabMaintenance: '🛠️ Maintenance',
  tabMyRequests: '📋 My Requests',
  roomNumberLabel: 'Room Number',
  roomNumberPlaceholder: 'e.g. 302 or Villa 5',
  guestNameLabel: 'Guest Name',
  guestPhoneLabel: 'Phone Number',
  selectItemsTitle: 'Select Items Needed (Tap to select)',
  customNotesLabel: 'Additional Notes / Instructions',
  customNotesPlaceholder: 'e.g. feather pillows, extra warm blanket, deliver at 6 PM',
  submitHousekeepingBtn: 'Send Request to Housekeeping',
  selectIssueTitle: 'Select Issue Category (Tap to select)',
  issueDescriptionLabel: 'Describe the Issue / Problem',
  issueDescriptionPlaceholder: 'e.g. leaking air conditioner, no hot water, light bulb broken',
  submitMaintenanceBtn: 'Submit Maintenance Report',
  requestSuccessTitle: 'Request Sent Successfully!',
  requestSuccessDesc: 'Our team has received your request and is attending to it shortly.',
  noActiveRequests: 'No active room service or maintenance requests.',
  statusReqPending: 'Pending',
  statusReqInProgress: 'In Progress',
  statusReqCompleted: 'Completed',
  requestReceivedBanner: 'Request received by staff',
  newRequestBtn: '+ New Request',
  roomNumberRequired: 'Please provide your room number',
  selectAtLeastOneItem: 'Please select at least one item or enter details in notes',
  describeIssueRequired: 'Please select an issue category or describe the problem',
  latestRequestsHeader: 'Recent Requests',
  refreshStatusBtn: '🔄 Refresh Status',
  waitingStaffAccept: 'Awaiting Staff',
  teamHousekeeping: 'Housekeeping Team',
  teamMaintenance: 'Maintenance Team',
  cancelRequestBtn: '🗑️ Cancel',
  tapQtyHint: '(Tap +/- to adjust quantity)',
  submittingRequest: 'Submitting request...',
  submittingMaintenance: 'Submitting report...',
  confirmCancelRequest: 'Are you sure you want to cancel this request?',
  cannotCancelRequest: 'Unable to cancel this request',
  errorCancelRequest: 'Failed to cancel request: ',
  submitRequestFailed: 'Failed to submit request. Please try again.',
  submitMaintenanceFailed: 'Failed to submit report. Please try again.',
  maxItemLimit: 'Max {max} per request',
  maxLimitReached: 'Maximum limit is {max} per request',
  // Restaurant Menu & Ordering
  menuOrderMode: '📋 Order Food',
  menuReserveMode: '🪑 Reserve Table',
  filterVeg: '🌿 Vegetarian',
  filterSpicy: '🌶 Spicy',
  filterGf: '🌾 Gluten-free',
  addToCart: 'Add',
  viewCart: 'View Order',
  cartEmpty: 'Cart is empty',
  deliverToRoom: '🛏️ Deliver to room',
  dineIn: '🍽️ Dine in',
  chargeToRoom: '🧾 Charge to room',
  payAtTable: '💳 Pay at table',
  orderSubtotal: 'Subtotal',
  orderServiceCharge: 'Service charge (10%)',
  orderGrandTotal: 'Grand Total',
  placeOrder: 'Place Order',
  orderReceived: '✅ Order Received!',
  orderEta: '⏱ Estimated time ~25–35 minutes',
  soldOut: 'Sold out',
  yourOrder: 'Your Order',
  orderDeliveryOption: 'Delivery Option',
  orderPaymentMethod: 'Payment Method',
  addMoreItems: 'Add more',
  menuLoading: 'Loading menu...',
  menuEmptyTitle: 'No Menu Available',
  menuEmptySubtitle: 'This restaurant has not added any menu items yet.',
  orderHistoryTitle: 'Order History',
  orderMoreBtn: '🛒 Order More',
  viewOrderHistoryBtn: '📋 View Order Status',
  roomNumberInput: 'Room Number (e.g. 204)',
  tableNumberInput: 'Table Number (e.g. T-05)',
  tableNumberRequired: 'Please specify table number',
  statusOrderReceived: 'Order Received',
  statusOrderPreparing: 'Preparing',
  statusOrderReady: 'Ready to Serve',
  statusOrderOnTheWay: 'On the way',
  statusOrderDelivered: 'Delivered',
  noOrderHistory: 'No order history yet',
  orderTotal: 'Total',
  refreshBtn: 'Refresh',
  roomBadge: 'Room',
  tableBadge: 'Table',
  activeOrderTracking: 'Track Order',
  activeOrderPreparing: 'Preparing your order...',
  activeOrderReady: 'Ready to serve',
  activeOrderOnTheWay: 'On the way...',

  // Generic Multi-Industry Services
  serviceUnavailable: 'This service is currently unavailable',
  selectProvider: 'Select Specialist / Provider',
  selectLocation: 'Select Branch / Location',
  selectTimeSlot: 'Select Time Slot',
  noSlots: 'No available slots for the selected date',
  requestSubmitted: 'Request submitted successfully',
  quotationPending: 'Quotation request is pending review',
  comingSoon: 'Coming Soon',
  serviceComingSoonDesc: 'This service is currently being prepared',
  termItem: 'Service Item',
  termProvider: 'Provider',
  termLocation: 'Location',
  termCustomer: 'Customer',

  // Onboarding Free Tier Application
  onboardTitle: 'Apply for Revio Free Tier',
  onboardSubtitle: 'Free forever for boutique hotels and resorts with up to 25 rooms',
  onboardQuotaActiveBadge: '{slots} of {max} slots remaining',
  onboardQuotaFullBadge: 'Quota Full (50/50) — Apply for Waiting List',
  onboardHotelNameLabel: 'Property / Hotel Name',
  onboardHotelNamePlaceholder: 'e.g. Baan Suan Resort',
  onboardTenantCodeLabel: 'System Code (lowercase alphanumeric)',
  onboardTenantCodePlaceholder: 'e.g. baansuan-resort',
  onboardContactPersonLabel: 'Contact Person Name',
  onboardContactPersonPlaceholder: 'e.g. John Doe',
  onboardContactPhoneLabel: 'Contact Phone Number',
  onboardContactPhonePlaceholder: 'e.g. +66 81 234 5678',
  onboardContactEmailLabel: 'Notification Email',
  onboardContactEmailPlaceholder: 'e.g. hotel@example.com',
  onboardRoomCountLabel: 'Total Room Count',
  onboardRoomCountHelp: 'Free Tier is capped at a maximum of 25 rooms',
  onboardProvinceLabel: 'Province / Location',
  onboardProvincePlaceholder: 'e.g. Chiang Mai, Phuket, Bangkok',
  onboardSelectProvincePlaceholder: '-- Select Province --',
  onboardProvinceOther: 'Other / International',
  onboardSubmitButton: 'Submit Free Tier Application',
  onboardSubmitting: 'Submitting application...',
  onboardSuccessTitle: 'Application Approved!',
  onboardPendingTitle: 'Application Received (Pending Approval)',
  onboardPendingNotice: 'Your application has been received and is currently under review by our team. Upon approval, your login credentials will be dispatched to your email and LINE.',
  onboardWaitingTitle: 'Application Added to Waiting List',
  onboardQueueNumberLabel: 'Queue Number',
  onboardFeatureStayTitle: 'PMS Booking System (Up to 25 rooms)',
  onboardFeatureDiningTitle: 'Dining & Room Service (10 tables, 150 orders/mo)',
  onboardFeatureSpaTitle: 'Spa & Wellness (2 beds, 30 bookings/mo)',
  onboardFeatureMiceTitle: 'MICE & Seminar RFP (15 inquiries/mo)',
  onboardFeatureAiTitle: 'AI Concierge (100 messages/mo)',
  onboardClosingWindow: 'Closing window and returning to LINE in 1 second...',
  onboardCredentialsSentNotice: 'Admin credentials (Username / Password) have been sent to your LINE and Email.',
  onboardCloseNowButton: 'Close Window Now',
  onboardSectionHotel: 'Property / Hotel Information',
  onboardSectionAdmin: 'Administrator Information',
  onboardRequiredFieldsError: 'Please fill in all required fields (*)',
  onboardMaxRoomsError: 'Free Tier is capped at 25 rooms. For larger properties, please choose Standard or Enterprise.',
  onboardTermsLabel: 'I have read and agree to Revio Free Tier Terms of Service and Privacy Policy',
  onboardTermsReadLink: 'Read Full Terms & Conditions',
  onboardTermsModalTitle: 'Revio Free Tier Terms & Conditions',
  onboardTermsModalClose: 'Close',
  onboardTermsModalAccept: 'I Understand & Accept',
  onboardTermsRequiredError: 'Please accept the terms and conditions before submitting',
  onboardTermsSummaryScope: 'Scope: Free usage up to 25 rooms / 10 dining tables with AI functions and LINE OA connection',
  onboardTermsSummaryInactivity: 'Fair Inactivity Policy: Inactive accounts over 30 days may be reallocated to businesses on the Waiting List',
  onboardTermsSummaryNoFee: '100% Free: No hidden fees, no credit card required upfront, and no retroactive charges',
  onboardTermsSummaryDataPrivacy: 'Data Ownership: All guest and booking data remain your exclusive property compliant with PDPA/GDPR',
  onboardTermsSummaryAsIs: 'Service Terms: Provided on an "As-Is" basis without commercial enterprise SLA guarantees',
};


// Chinese translations
const zh: Translations = {
  appName: '预订房间',
  loading: '加载中...',
  error: '错误',
  retry: '重试',
  back: '返回',
  next: '下一步',
  confirm: '确认',
  cancel: '取消',
  close: '关闭',
  done: '完成',
  selectLanguage: '选择语言',
  backToTop: '回到顶部',

  searchTitle: '搜索并预订',
  checkIn: '入住日期',
  checkOut: '退房日期',
  adults: '成人',
  children: '儿童',
  rooms: '房间',
  searchButton: '搜索并预订',
  viewAvailabilityCalendar: '查看空房日历',
  availabilityCalendar: '空房日历',
  available: '有空房',
  notAvailable: '无空房',
  allRooms: '所有房型',
  loadingCalendar: '正在加载日历...',
  prevMonth: '◀ 上个月',
  nextMonth: '下个月 ▶',

  roomsTitle: '选择房间',
  perNight: '/晚',
  totalFor: '共',
  inclTaxService: '(含税及服务费)',
  nights: '晚',
  selectRoom: '选择此房间',
  noRoomsAvailable: '所选日期无可用房间',
  changeSearch: '编辑',
  amenities: '设施',
  cancellationPolicy: '取消政策',
  mealPlan: '餐饮计划',

  guestInfoTitle: '住客信息',
  spaGuestInfoTitle: '服务客人信息',
  diningGuestInfoTitle: '预订人信息',
  registrantInfoTitle: '报名人员信息',
  requesterInfoTitle: '申请人信息',
  registrantName: '报名人姓名',
  requesterName: '申请人姓名',
  seminarCourse: '课程 / 研讨会主题',
  speaker: '主讲人 / 讲师',
  cpdHours: 'CPD 学时',
  confirmRegistration: '确认报名',
  registrationSuccess: '报名成功！',
  firstName: '名',
  lastName: '姓',
  email: '电子邮箱',
  phone: '电话号码',
  specialRequests: '特殊要求（可选）',
  guestTitle: '称谓',
  titleMr: '先生',
  titleMrs: '女士',
  titleMs: '女士',
  titleMiss: '小姐',
  titleDr: '博士',
  selectTitle: '-- 选择称谓 --',
  nationality: '国籍',
  nationalityPlaceholder: '例如: 泰国, 中国, 英国',
  idType: '证件类型',
  idTypePassport: '护照',
  idTypeNationalId: '身份证',
  idTypeDrivingLicense: '驾驶证',
  idNumber: '证件号码',
  idNumberPlaceholder: '请输入证件号码',
  selectIdType: '-- 选择证件类型 --',

  confirmTitle: '确认预订',
  bookingSummary: '预订摘要',
  roomType: '房型',
  guestName: '住客姓名',
  totalAmount: '总金额',
  confirmBooking: '确认预订',
  spaDetailsTitle: '水疗详情',
  spaTreatment: '护理项目',
  spaOutlet: '水疗中心',
  spaRate: '护理费用',
  diningDetailsTitle: '订座详情',
  diningOutlet: '餐厅',
  seatingArea: '用餐区域',
  serviceDate: '服务日期',
  timeSlot: '时间段',
  partySize: '就餐人数',
  guestsCount: '服务人数',
  minutesUnit: '分钟',
  personUnit: '位',
  roomPrefix: '房间',
  roomNumber: '房间号',
  freeReservation: '免费预订',

  successTitle: '预订成功！',
  bookingConfirmed: '您的预订已确认',
  confirmationNumber: '确认号',
  checkInTime: '入住时间：下午2:00',
  checkOutTime: '退房时间：中午12:00',
  viewInChat: '在LINE聊天中查看',
  confirmationSentToChat: '确认信息已发送至LINE聊天，正在返回聊天...',
  returnToChatWeb: '您的预订已确认。点击下方按钮关闭此窗口并返回聊天。',
  returnToWhatsAppChat: '您的预订已确认。点击下方按钮返回聊天。',
  returnToWhatsAppBtn: '返回 WhatsApp 聊天',
  returnToWeChatChat: '您的预订已确认。点击下方按钮关闭页面返回微信。',
  returnToWeChatBtn: '关闭页面返回微信',
  closeWindowBtn: '关闭此页面',
  returnToServicesBtn: '返回首页 / 服务列表',

  errorGeneric: '发生错误，请重试。',
  errorNetwork: '连接失败，请检查网络。',
  errorNoRooms: '所选日期无房间。',
  errorBookingFailed: '预订失败，请重试。',

  required: '此字段为必填项',
  invalidEmail: '邮箱地址无效',
  invalidPhone: '电话号码无效',
  selectDates: '请选择日期',

  home: '首页',
  stepSearch: '搜索',
  stepRoom: '房间',
  stepGuest: '信息',
  stepConfirm: '确认',

  payAtHotel: '到店付款',

  // My Bookings
  myBookings: '我的预订',
  bookings: '订单',
  noBookings: '暂无预订',
  noBookingsDesc: '您还没有预订记录',
  startBooking: '开始预订',
  statusConfirmed: '已确认',
  statusCancelled: '已取消',
  statusCompleted: '已完成',
  statusPending: '待处理',

  capacityExceeded: '超出容量限制',
  capacityExceededMessage: '客人数量超过房间容量限制。您是否愿意支付额外费用？',
  extraAdults: '额外成人',
  extraChildren: '额外儿童',
  person: '人',
  personPerNight: '人/晚',
  totalExtraCharge: '总额外费用',
  extraCharges: '额外费用',
  roomRate: '房费',
  roomRateBeforeTax: '房费（税前）',
  serviceRateBeforeTax: '服务费（税前）',
  serviceCharge10: '服务费 10%',
  vat7: '增值税 7%',
  ratePlan: '价格方案',
  selectRatePlan: '选择价格方案',
  hideRates: '隐藏价格',
  guests: '客人',
  refundPolicy: '退款政策',
  nonRefundablePolicy: '不可退款',
  refundablePolicy: '免费取消',
  autoFilledFromPrevious: '信息已从您上次预订中自动填写',
  editingBooking: '编辑预订',
  confirmEditBooking: '确认更新预订?',
  confirmCreateBooking: '确认此预订?',

  // Room Types Browse Page
  roomTypesTitle: '房型',
  startingFrom: '起价',
  checkAvailability: '查看房间空房',
  ourRooms: '我们的房间',
  excludingTaxes: '不含税费及服务费',

  // Payment
  paymentTitle: '付款',
  payWithCard: '信用卡/借记卡支付',
  payWithPromptPay: 'PromptPay 支付',
  processingPayment: '正在处理付款...',
  paymentSuccessful: '付款成功！',
  paymentFailed: '付款失败',
  paymentError: '付款出错，请重试。',
  retryPayment: '重试付款',
  skipPayment: '到店付款',
  payNow: '立即付款',
  paymentWarningBanner: '您的预订已确认。请完成付款或选择"到店付款"',
  paymentNoGoBack: '无法返回',
  paymentBackWarning: '预订已确认\n请立即付款或选择\n"到店付款"',
  statusPaid: '已在线付款',

  // Booking Management
  editBooking: '编辑预订',
  editModeBanner: '修改预订模式',
  cancelEdit: '取消修改',
  cancelBooking: '取消预订',
  cannotModifyBooking: '无法修改或取消（少于3天）。请直接联系酒店。',
  saveChanges: '保存更改',
  confirmCancelBooking: '您确定要取消此预订吗？',
  keepBooking: '保留预订',
  confirmCancel: '确认取消',
  bookingUpdatedSuccess: '预订更新成功',
  bookingCancelledSuccess: '预订取消成功',
  errorLoadingBooking: '加载预订详情失败',
  errorUpdatingBooking: '更新预订失败',
  errorCancellingBooking: '取消预订失败',
  pleaseFillAllFields: '请填写所有必填字段',
  specialRequestsPlaceholder: '例如：高楼层、加床等',
  cancellationPolicy3Days: '必须在入住前至少3天取消',
  bookingWillBeCancelled: '预订将被取消且无法恢复',
  actionCannotBeUndone: '此操作无法撤消！',
  saving: '保存中',
  checkOutMustBeAfterCheckIn: '退房日期必须晚于入住日期',
  cannotModifyLessThan3Days: '无法修改预订，剩余时间少于3天。请直接联系酒店',
  roomNotAvailable: '所选日期房间不可用。请选择其他日期',
  bookingNotFound: '未找到预订',
  connectionError: '连接错误。请检查您的网络并重试',
  nonRefundable: '⛔ 不可退款',
  nonRefundableWarning: '此价格为不可退款。不可取消，且仅支持在线支付。',
  nonRefundableCannotModify: '不可退款价格无法修改或取消。请直接联系酒店。',
  paidBookingCannotModify:
    '此预订已付款。如需修改或取消，请直接联系酒店。',

  // Room Service & Maintenance
  roomServiceTitle: '客房服务与报修',
  roomServiceSubtitle: '申请客房用品或提交房间设施维修请求',
  tabHousekeeping: '🧹 客房用品',
  tabMaintenance: '🛠️ 设施报修',
  tabMyRequests: '📋 我的请求',
  roomNumberLabel: '房间号码',
  roomNumberPlaceholder: '例如 302 或 别墅 5',
  guestNameLabel: '住客姓名',
  guestPhoneLabel: '联系电话',
  selectItemsTitle: '选择所需用品 (点击选择)',
  customNotesLabel: '备注 / 特殊要求',
  customNotesPlaceholder: '例如：羽绒枕头、加厚毛毯、请于下午6点送达',
  submitHousekeepingBtn: '发送客房用品请求',
  selectIssueTitle: '选择故障类型 (点击选择)',
  issueDescriptionLabel: '故障问题详情描述',
  issueDescriptionPlaceholder: '例如：空调漏水、淋浴没有热水、卫生间灯泡不亮',
  submitMaintenanceBtn: '提交设施报修',
  requestSuccessTitle: '请求已成功提交！',
  requestSuccessDesc: '我们的服务人员已收到您的请求，将尽快为您处理。',
  noActiveRequests: '暂无客房服务或报修记录。',
  statusReqPending: '待处理',
  statusReqInProgress: '处理中',
  statusReqCompleted: '已完成',
  requestReceivedBanner: '工作人员已接单处理',
  newRequestBtn: '+ 提交新请求',
  roomNumberRequired: '请输入您的房间号码',
  selectAtLeastOneItem: '请至少选择一项所需用品，或在备注中说明',
  describeIssueRequired: '请选择故障类型或填写问题描述',
  latestRequestsHeader: '最近请求列表',
  refreshStatusBtn: '🔄 刷新状态',
  waitingStaffAccept: '等待服务人员接单',
  teamHousekeeping: '客房清洁团队',
  teamMaintenance: '工程维修团队',
  cancelRequestBtn: '🗑️ 取消',
  tapQtyHint: '(点击 +/- 调整数量)',
  submittingRequest: '正在提交请求...',
  submittingMaintenance: '正在提交报修...',
  confirmCancelRequest: '您确定要取消此项请求吗？',
  cannotCancelRequest: '无法取消此请求',
  errorCancelRequest: '取消请求时出错：',
  submitRequestFailed: '提交请求失败，请重试',
  submitMaintenanceFailed: '提交报修失败，请重试',
  maxItemLimit: '每单最多可申请 {max} 件',
  maxLimitReached: '每单最多只可申请 {max} 件',
  // Restaurant Menu & Ordering
  menuOrderMode: '📋 点餐',
  menuReserveMode: '🪑 预订餐桌',
  filterVeg: '🌿 素食',
  filterSpicy: '🌶 辣',
  filterGf: '🌾 无麸质',
  addToCart: '加入',
  viewCart: '查看订单',
  cartEmpty: '购物车为空',
  deliverToRoom: '🛏️ 送到房间',
  dineIn: '🍽️ 堂食',
  chargeToRoom: '🧾 记房费',
  payAtTable: '💳 在餐桌结账',
  orderSubtotal: '小计',
  orderServiceCharge: '服务费 (10%)',
  orderGrandTotal: '总计',
  placeOrder: '下单',
  orderReceived: '✅ 已收到订单！',
  orderEta: '⏱ 预计时间约25–35分钟',
  soldOut: '售罄',
  yourOrder: '您的订单',
  orderDeliveryOption: '用餐方式',
  orderPaymentMethod: '付款方式',
  addMoreItems: '继续点餐',
  menuLoading: '正在加载菜单...',
  menuEmptyTitle: '暂无菜单',
  menuEmptySubtitle: '该餐厅尚未添加任何菜单。',
  orderHistoryTitle: '点餐记录',
  orderMoreBtn: '🛒 继续点餐',
  viewOrderHistoryBtn: '📋 查看订单状态',
  roomNumberInput: '房间号 (如 204)',
  tableNumberInput: '桌号 (如 T-05)',
  tableNumberRequired: '请填写桌号',
  statusOrderReceived: '已接单',
  statusOrderPreparing: '备餐中',
  statusOrderReady: '餐点已备好',
  statusOrderOnTheWay: '送餐中',
  statusOrderDelivered: '已送达',
  noOrderHistory: '暂无订单记录',
  orderTotal: '合计',
  refreshBtn: '刷新',
  roomBadge: '房间',
  tableBadge: '餐桌',
  activeOrderTracking: '追踪订单',
  activeOrderPreparing: '正在备餐...',
  activeOrderReady: '餐点已备好，准备上餐',
  activeOrderOnTheWay: '正在配送...',

  // Generic Multi-Industry Services
  serviceUnavailable: '此服务暂未开放',
  selectProvider: '选择专员 / 服务人员',
  selectLocation: '选择分店 / 地点',
  selectTimeSlot: '选择预约时间段',
  noSlots: '所选日期暂无可预约时间段',
  requestSubmitted: '服务申请已提交',
  quotationPending: '报价方案正在审核处理中',
  comingSoon: '即将推出',
  serviceComingSoonDesc: '该服务正在筹备中，敬请期待',
  termItem: '服务项目',
  termProvider: '服务人员',
  termLocation: '分店地点',
  termCustomer: '客户',

  // Onboarding Free Tier Application
  onboardTitle: '申请 Revio 免费版 (Free Tier)',
  onboardSubtitle: '专为25间客房以下的精品酒店设计，终身免费使用',
  onboardQuotaActiveBadge: '立即可用名额 {slots} / {max} 家',
  onboardQuotaFullBadge: '名额已满 (50/50) — 可加入候补队列',
  onboardHotelNameLabel: '酒店 / 民宿名称',
  onboardHotelNamePlaceholder: '例如：水岸花园度假酒店',
  onboardTenantCodeLabel: '系统代号 (小写英数字)',
  onboardTenantCodePlaceholder: '例如：baansuan-resort',
  onboardContactPersonLabel: '管理员姓名',
  onboardContactPersonPlaceholder: '例如：张伟',
  onboardContactPhoneLabel: '联系电话',
  onboardContactPhonePlaceholder: '例如：+66 81 234 5678',
  onboardContactEmailLabel: '通知邮箱',
  onboardContactEmailPlaceholder: '例如：hotel@example.com',
  onboardRoomCountLabel: '总客房数',
  onboardRoomCountHelp: '免费版最多支持 25 间客房',
  onboardProvinceLabel: '所在省份 / 城市',
  onboardProvincePlaceholder: '例如：清迈、普吉岛、曼谷',
  onboardSelectProvincePlaceholder: '-- 请选择省份 --',
  onboardProvinceOther: '其他 / 海外',
  onboardSubmitButton: '提交免费版申请',
  onboardSubmitting: '正在提交...',
  onboardSuccessTitle: '申请成功已开通！',
  onboardPendingTitle: '已收到申请（等待审核）',
  onboardPendingNotice: '我们已收到您的免费版申请，目前正在由系统管理员审核中。审核通过后，账号和密码将立即发送至您的邮箱和LINE。',
  onboardWaitingTitle: '已成功登记至候补队列',
  onboardQueueNumberLabel: '排队号',
  onboardFeatureStayTitle: 'PMS客房预订系统 (最高 25 间)',
  onboardFeatureDiningTitle: '餐饮点餐 & 送餐 (10 张桌，150 笔/月)',
  onboardFeatureSpaTitle: '水疗按摩预约 (2 张床，30 笔/月)',
  onboardFeatureMiceTitle: '会议场地询价 RFP (15 笔/月)',
  onboardFeatureAiTitle: '智能 AI 礼宾客服 (100 条消息/月)',
  onboardClosingWindow: '正在关闭窗口并在1秒内返回LINE...',
  onboardCredentialsSentNotice: '管理员账号信息（账号/密码）已发送至您的LINE和邮箱。',
  onboardCloseNowButton: '立即关闭窗口',
  onboardSectionHotel: '住宿 / 酒店信息',
  onboardSectionAdmin: '管理员信息',
  onboardRequiredFieldsError: '请填写所有必填项 (*)',
  onboardMaxRoomsError: '免费版最多支持 25 间客房。如需更多，请选择标准版或企业版。',
  onboardTermsLabel: '我已阅读并同意 Revio 免费版服务条款和隐私政策',
  onboardTermsReadLink: '阅读完整条款与条件',
  onboardTermsModalTitle: 'Revio 免费版服务条款与条件',
  onboardTermsModalClose: '关闭',
  onboardTermsModalAccept: '我理解并接受条款',
  onboardTermsRequiredError: '请在提交申请前同意服务条款与条件',
  onboardTermsSummaryScope: '使用范围：最多支持 25 间客房 / 10 张餐桌，包含 AI 功能与 LINE OA 渠道连接',
  onboardTermsSummaryInactivity: '活跃度政策：连续 30 天无活跃记录的账户可能会被重新分配给候补名单上的企业',
  onboardTermsSummaryNoFee: '完全免费：无任何隐藏费用，无需提前绑定信用卡，绝无追溯收费',
  onboardTermsSummaryDataPrivacy: '数据所有权：所有宾客及预订数据均为您的专有财产，符合隐私与数据保护法规',
  onboardTermsSummaryAsIs: '服务基础：本服务按“现状”提供，不包含企业级 SLA 保证',
};


// Russian translations
const ru: Translations = {
  appName: 'Бронирование номеров',
  loading: 'Загрузка...',
  error: 'Ошибка',
  retry: 'Повторить',
  back: 'Назад',
  next: 'Далее',
  confirm: 'Подтвердить',
  cancel: 'Отмена',
  close: 'Закрыть',
  done: 'Готово',
  selectLanguage: 'Выберите язык',
  backToTop: 'Наверх',

  searchTitle: 'Поиск и бронирование',
  checkIn: 'Заезд',
  checkOut: 'Выезд',
  adults: 'Взрослые',
  children: 'Дети',
  rooms: 'Номера',
  searchButton: 'Найти номера',
  viewAvailabilityCalendar: 'КАЛЕНДАРЬ ДОСТУПНОСТИ',
  availabilityCalendar: 'КАЛЕНДАРЬ ДОСТУПНОСТИ',
  available: 'Свободно',
  notAvailable: 'Занято',
  allRooms: 'Все номера',
  loadingCalendar: 'Загрузка календаря...',
  prevMonth: '◀ Пред.',
  nextMonth: 'След. ▶',

  roomsTitle: 'Выбор номера',
  perNight: '/ночь',
  totalFor: 'Итого за',
  inclTaxService: '(включая налоги и сборы)',
  nights: 'ноч.',
  selectRoom: 'Выбрать номер',
  noRoomsAvailable: 'Нет доступных номеров на выбранные даты',
  changeSearch: 'Изменить даты',
  amenities: 'Удобства',
  cancellationPolicy: 'Политика отмены',
  mealPlan: 'План питания',

  guestInfoTitle: 'Данные гостя',
  spaGuestInfoTitle: 'Данные гостя (Спа)',
  diningGuestInfoTitle: 'Данные гостя (Ресторан)',
  registrantInfoTitle: 'Данные участника',
  requesterInfoTitle: 'Данные заявителя',
  registrantName: 'Имя участника',
  requesterName: 'Имя заявителя',
  seminarCourse: 'Курс / Тема семинара',
  speaker: 'Лектор / Докладчик',
  cpdHours: 'Часы CPD',
  confirmRegistration: 'Подтвердить регистрацию',
  registrationSuccess: 'Регистрация подтверждена!',
  firstName: 'Имя',
  lastName: 'Фамилия',
  email: 'Электронная почта',
  phone: 'Номер телефона',
  specialRequests: 'Особые пожелания (необязательно)',
  guestTitle: 'Обращение',
  titleMr: 'Г-н',
  titleMrs: 'Г-жа',
  titleMs: 'Мс',
  titleMiss: 'Мисс',
  titleDr: 'Д-р',
  selectTitle: '-- Выберите обращение --',
  nationality: 'Гражданство',
  nationalityPlaceholder: 'Например: Россия, Казахстан',
  idType: 'Тип документа',
  idTypePassport: 'Загранпаспорт',
  idTypeNationalId: 'Национальный паспорт',
  idTypeDrivingLicense: 'Водительские права',
  idNumber: 'Номер документа',
  idNumberPlaceholder: 'Введите номер документа',
  selectIdType: '-- Выберите тип документа --',

  confirmTitle: 'Подтверждение бронирования',
  bookingSummary: 'Детали бронирования',
  roomType: 'Тип номера',
  guestName: 'Имя гостя',
  totalAmount: 'Итоговая сумма',
  confirmBooking: 'Подтвердить бронирование',
  spaDetailsTitle: 'Детали процедуры',
  spaTreatment: 'Процедура',
  spaOutlet: 'Спа-салон',
  spaRate: 'Стоимость процедуры',
  diningDetailsTitle: 'Детали бронирования столика',
  diningOutlet: 'Ресторан',
  seatingArea: 'Зона посадки',
  serviceDate: 'Дата',
  timeSlot: 'Время',
  partySize: 'Количество персон',
  guestsCount: 'Количество гостей',
  minutesUnit: 'мин.',
  personUnit: 'чел.',
  roomPrefix: 'Номер',
  roomNumber: 'Номер комнаты',
  freeReservation: 'Бесплатное бронирование',

  successTitle: 'Бронирование подтверждено!',
  bookingConfirmed: 'Ваше бронирование успешно подтверждено',
  confirmationNumber: 'Номер подтверждения',
  checkInTime: 'Заезд: 14:00',
  checkOutTime: 'Выезд: 12:00',
  viewInChat: 'Посмотреть в чате LINE',
  confirmationSentToChat: 'Подтверждение отправлено в чат LINE...',
  returnToChatWeb: 'Ваше бронирование подтверждено. Нажмите кнопку ниже, чтобы закрыть страницу и вернуться в чат.',
  returnToWhatsAppChat: 'Ваше бронирование подтверждено. Нажмите кнопку ниже, чтобы вернуться в WhatsApp.',
  returnToWhatsAppBtn: 'Вернуться в WhatsApp',
  returnToWeChatChat: 'Ваше бронирование подтверждено. Нажмите кнопку ниже, чтобы вернуться в WeChat.',
  returnToWeChatBtn: 'Вернуться в WeChat',
  closeWindowBtn: 'Закрыть это окно',
  returnToServicesBtn: 'Вернуться на главную / к услугам',

  errorGeneric: 'Произошла ошибка. Пожалуйста, попробуйте снова.',
  errorNetwork: 'Сбой подключения. Проверьте интернет-соединение.',
  errorNoRooms: 'Нет доступных номеров на выбранные даты.',
  errorBookingFailed: 'Не удалось оформить бронирование. Попробуйте еще раз.',

  required: 'Обязательное поле',
  invalidEmail: 'Неверный адрес эл. почты',
  invalidPhone: 'Неверный номер телефона',
  selectDates: 'Пожалуйста, выберите даты',

  home: 'Главная',
  stepSearch: 'Поиск',
  stepRoom: 'Номер',
  stepGuest: 'Данные',
  stepConfirm: 'Итог',

  payAtHotel: 'Оплата в отеле',

  // My Bookings
  myBookings: 'Мои бронирования',
  bookings: 'бронир.',
  noBookings: 'Нет активных бронирований',
  noBookingsDesc: 'У вас пока нет истории бронирований',
  startBooking: 'Начать бронирование',
  statusConfirmed: 'Подтверждено',
  statusCancelled: 'Отменено',
  statusCompleted: 'Завершено',
  statusPending: 'В обработке',

  capacityExceeded: 'Превышена вместимость',
  capacityExceededMessage: 'Количество гостей превышает вместимость номера. Желаете продолжить с доплатой?',
  extraAdults: 'Доп. взрослые',
  extraChildren: 'Доп. дети',
  person: 'чел.',
  personPerNight: 'чел./ночь',
  totalExtraCharge: 'Итого доплата',
  extraCharges: 'Доплаты',
  roomRate: 'Стоимость номера',
  roomRateBeforeTax: 'Стоимость номера (до налогов)',
  serviceRateBeforeTax: 'Сервисный сбор (до налогов)',
  serviceCharge10: 'Сервисный сбор 10%',
  vat7: 'НДС 7%',
  ratePlan: 'Тарифный план',
  selectRatePlan: 'Выбрать тарифный план',
  hideRates: 'Скрыть цены',
  guests: 'гостей',
  refundPolicy: 'Политика возврата',
  nonRefundablePolicy: 'Без возврата средств',
  refundablePolicy: 'Бесплатная отмена',
  autoFilledFromPrevious: 'Данные заполнены из вашего предыдущего бронирования',
  editingBooking: 'Редактирование бронирования',
  confirmEditBooking: 'Подтвердить изменение бронирования?',
  confirmCreateBooking: 'Подтвердить это бронирование?',

  // Room Types Browse Page
  roomTypesTitle: 'Типы номеров',
  startingFrom: 'от',
  checkAvailability: 'Проверить доступность',
  ourRooms: 'Наши номера',
  excludingTaxes: 'Без учета налогов и сервисного сбора',

  // Payment
  paymentTitle: 'Оплата',
  payWithCard: 'Оплатить картой',
  payWithPromptPay: 'Оплатить через PromptPay',
  processingPayment: 'Обработка платежа...',
  paymentSuccessful: 'Оплата прошла успешно!',
  paymentFailed: 'Ошибка оплаты',
  paymentError: 'Произошла ошибка при оплате. Пожалуйста, повторите попытку.',
  retryPayment: 'Повторить попытку',
  skipPayment: 'Оплата в отеле',
  payNow: 'Оплатить сейчас',
  paymentWarningBanner: 'Бронирование подтверждено. Пожалуйста, завершите оплату или выберите «Оплата в отеле»',
  paymentNoGoBack: 'Нельзя вернуться назад',
  paymentBackWarning: 'Бронирование подтверждено\nПожалуйста, оплатите сейчас или\nвыберите «Оплата в отеле»',
  statusPaid: 'Оплачено онлайн',

  // Booking Management
  editBooking: 'Изменить бронь',
  editModeBanner: 'Режим редактирования бронирования',
  cancelEdit: 'Отменить редактирование',
  cancelBooking: 'Отменить бронирование',
  cannotModifyBooking: 'Изменение или отмена невозможны (осталось менее 3 дней). Пожалуйста, свяжитесь с отелем напрямую.',
  saveChanges: 'Сохранить изменения',
  confirmCancelBooking: 'Вы уверены, что хотите отменить это бронирование?',
  keepBooking: 'Оставить бронь',
  confirmCancel: 'Подтвердить отмену',
  bookingUpdatedSuccess: 'Бронирование успешно обновлено',
  bookingCancelledSuccess: 'Бронирование успешно отменено',
  errorLoadingBooking: 'Не удалось загрузить данные бронирования',
  errorUpdatingBooking: 'Не удалось обновить бронирование',
  errorCancellingBooking: 'Не удалось отменить бронирование',
  pleaseFillAllFields: 'Пожалуйста, заполните все обязательные поля',
  specialRequestsPlaceholder: 'Например: высокий этаж, дополнительная кровать и т.д.',
  cancellationPolicy3Days: 'Отмена должна быть произведена не менее чем за 3 дня до заезда',
  bookingWillBeCancelled: 'Бронирование будет отменено и не подлежит восстановлению',
  actionCannotBeUndone: 'Это действие невозможно отменить!',
  saving: 'Сохранение',
  checkOutMustBeAfterCheckIn: 'Дата выезда должна быть позже даты заезда',
  cannotModifyLessThan3Days: 'Невозможно изменить бронирование менее чем за 3 дня. Обратитесь в отель.',
  roomNotAvailable: 'Номер недоступен на выбранные даты. Пожалуйста, выберите другие даты',
  bookingNotFound: 'Бронирование не найдено',
  connectionError: 'Ошибка соединения. Проверьте интернет и повторите попытку',
  nonRefundable: '⛔ Без возврата',
  nonRefundableWarning: 'Этот тариф не подлежит возврату. Отмена невозможна, требуется предоплата онлайн.',
  nonRefundableCannotModify: 'Тариф без возврата не может быть изменен или отменен. Обратитесь в отель.',
  paidBookingCannotModify: 'Это бронирование уже оплачено. Для изменения или отмены обратитесь в отель.',

  // Room Service & Maintenance
  roomServiceTitle: 'Обслуживание и сервис',
  roomServiceSubtitle: 'Заказ предметов обихода или заявка на ремонт в номере',
  tabHousekeeping: '🧹 Уборка и вещи',
  tabMaintenance: '🛠️ Ремонт',
  tabMyRequests: '📋 Мои запросы',
  roomNumberLabel: 'Номер комнаты (Room No.)',
  roomNumberPlaceholder: 'Например: 302 или Villa 5',
  guestNameLabel: 'Имя гостя (Guest Name)',
  guestPhoneLabel: 'Номер телефона (Phone No.)',
  selectItemsTitle: 'Выберите нужные предметы (нажмите для выбора)',
  customNotesLabel: 'Дополнительные примечания / пожелания',
  customNotesPlaceholder: 'Например: перьевые подушки, теплое одеяло, доставка в 18:00',
  submitHousekeepingBtn: 'Отправить запрос в службу уборки',
  selectIssueTitle: 'Категория неисправности (нажмите для выбора)',
  issueDescriptionLabel: 'Описание проблемы',
  issueDescriptionPlaceholder: 'Например: течет кондиционер, нет горячей воды, перегорела лампа',
  submitMaintenanceBtn: 'Отправить заявку на ремонт',
  requestSuccessTitle: 'Запрос успешно отправлен!',
  requestSuccessDesc: 'Персонал получил ваш запрос и приступит к выполнению в ближайшее время.',
  noActiveRequests: 'Нет активных запросов на обслуживание или ремонт.',
  statusReqPending: 'В ожидании',
  statusReqInProgress: 'Выполняется',
  statusReqCompleted: 'Выполнено',
  requestReceivedBanner: 'Запрос принят персоналом',
  newRequestBtn: '+ Новый запрос',
  roomNumberRequired: 'Пожалуйста, укажите номер комнаты',
  selectAtLeastOneItem: 'Пожалуйста, выберите хотя бы один предмет или укажите в примечаниях',
  describeIssueRequired: 'Пожалуйста, выберите категорию или опишите проблему',
  latestRequestsHeader: 'Недавние запросы',
  refreshStatusBtn: '🔄 Обновить статус',
  waitingStaffAccept: 'Ожидание персонала',
  teamHousekeeping: 'Служба уборки',
  teamMaintenance: 'Техническая служба',
  cancelRequestBtn: '🗑️ Отмена',
  tapQtyHint: '(Нажмите +/- для изменения количества)',
  submittingRequest: 'Отправка запроса...',
  submittingMaintenance: 'Отправка заявки...',
  confirmCancelRequest: 'Вы уверены, что хотите отменить этот запрос?',
  cannotCancelRequest: 'Невозможно отменить этот запрос',
  errorCancelRequest: 'Ошибка при отмене запроса: ',
  submitRequestFailed: 'Не удалось отправить запрос. Попробуйте еще раз.',
  submitMaintenanceFailed: 'Не удалось отправить заявку. Попробуйте еще раз.',
  maxItemLimit: 'Максимум {max} на один запрос',
  maxLimitReached: 'Достигнут максимальный лимит {max} на запрос',
  // Restaurant Menu & Ordering
  menuOrderMode: '📋 Заказать еду',
  menuReserveMode: '🪑 Забронировать столик',
  filterVeg: '🌿 Вегетарианское',
  filterSpicy: '🌶 Острое',
  filterGf: '🌾 Без глютена',
  addToCart: 'Добавить',
  viewCart: 'Посмотреть заказ',
  cartEmpty: 'Корзина пуста',
  deliverToRoom: '🛏️ Доставить в номер',
  dineIn: '🍽️ В зале',
  chargeToRoom: '🧾 Записать на счёт номера',
  payAtTable: '💳 Оплатить у столика',
  orderSubtotal: 'Подытог',
  orderServiceCharge: 'Сервисный сбор (10%)',
  orderGrandTotal: 'Итого',
  placeOrder: 'Оформить заказ',
  orderReceived: '✅ Заказ принят!',
  orderEta: '⏱ Ориентировочно ~25–35 минут',
  soldOut: 'Нет в наличии',
  yourOrder: 'Ваш заказ',
  orderDeliveryOption: 'Способ получения',
  orderPaymentMethod: 'Способ оплаты',
  addMoreItems: 'Добавить ещё',
  menuLoading: 'Загрузка меню...',
  menuEmptyTitle: 'Меню отсутствует',
  menuEmptySubtitle: 'В этом ресторане пока нет добавленных блюд.',
  orderHistoryTitle: 'История заказов',
  orderMoreBtn: '🛒 Заказать ещё',
  viewOrderHistoryBtn: '📋 Статус заказов',
  roomNumberInput: 'Номер комнаты (напр. 204)',
  tableNumberInput: 'Номер столика (напр. T-05)',
  tableNumberRequired: 'Пожалуйста, укажите номер столика',
  statusOrderReceived: 'Заказ принят',
  statusOrderPreparing: 'Готовится',
  statusOrderReady: 'Готово к подаче',
  statusOrderOnTheWay: 'В пути',
  statusOrderDelivered: 'Доставлен',
  noOrderHistory: 'История заказов пуста',
  orderTotal: 'Итого',
  refreshBtn: 'Обновить',
  roomBadge: 'Номер',
  tableBadge: 'Столик',
  activeOrderTracking: 'Отследить заказ',
  activeOrderPreparing: 'Готовим ваш заказ...',
  activeOrderReady: 'Готово к подаче',
  activeOrderOnTheWay: 'В пути...',

  // Generic Multi-Industry Services
  serviceUnavailable: 'Эта услуга временно недоступна',
  selectProvider: 'Выберите специалиста',
  selectLocation: 'Выберите филиал / отделение',
  selectTimeSlot: 'Выберите время',
  noSlots: 'На выбранную дату нет свободного времени',
  requestSubmitted: 'Заявка успешно отправлена',
  quotationPending: 'Запрос на расчет стоимости находится на рассмотрении',
  comingSoon: 'Скоро',
  serviceComingSoonDesc: 'Данная услуга находится в стадии подготовки',
  termItem: 'Услуга',
  termProvider: 'Специалист',
  termLocation: 'Филиал',
  termCustomer: 'Клиент',

  // Onboarding Free Tier Application
  onboardTitle: 'Регистрация Revio Free Tier',
  onboardSubtitle: 'Бесплатно для бутик-отелей и курортов до 25 номеров',
  onboardQuotaActiveBadge: 'Свободно мест: {slots} из {max}',
  onboardQuotaFullBadge: 'Квота заполнена (50/50) — запись в список ожидания',
  onboardHotelNameLabel: 'Название отеля',
  onboardHotelNamePlaceholder: 'например: Baan Suan Resort',
  onboardTenantCodeLabel: 'Код системы',
  onboardTenantCodePlaceholder: 'например: baansuan-resort',
  onboardContactPersonLabel: 'Контактное лицо',
  onboardContactPersonPlaceholder: 'например: Иван Иванов',
  onboardContactPhoneLabel: 'Телефон',
  onboardContactPhonePlaceholder: 'например: +66 81 234 5678',
  onboardContactEmailLabel: 'Эл. почта',
  onboardContactEmailPlaceholder: 'например: hotel@example.com',
  onboardRoomCountLabel: 'Всего номеров',
  onboardRoomCountHelp: 'Free Tier ограничен 25 номерами',
  onboardProvinceLabel: 'Провинция / Город',
  onboardProvincePlaceholder: 'например: Пхукет, Бангкок',
  onboardSelectProvincePlaceholder: '-- Выберите провинцию --',
  onboardProvinceOther: 'Другое / За границей',
  onboardSubmitButton: 'Подать заявку Free Tier',
  onboardSubmitting: 'Отправка заявки...',
  onboardSuccessTitle: 'Заявка одобрена!',
  onboardPendingTitle: 'Заявка получена (Ожидает рассмотрения)',
  onboardPendingNotice: 'Ваша заявка принята и находится на рассмотрении администратора. После одобрения логин и пароль будут отправлены на ваш email и в LINE.',
  onboardWaitingTitle: 'Добавлено в список ожидания',
  onboardQueueNumberLabel: 'Номер очереди',
  onboardFeatureStayTitle: 'PMS бронирования (до 25 номеров)',
  onboardFeatureDiningTitle: 'Ресторан и доставка (10 столиков, 150 заказов/мес)',
  onboardFeatureSpaTitle: 'Спа и массаж (2 места, 30 записей/мес)',
  onboardFeatureMiceTitle: 'MICE & Запросы (15 в месяц)',
  onboardFeatureAiTitle: 'AI Консьерж (100 сообщений/мес)',
  onboardClosingWindow: 'Закрытие окна и возврат в LINE через 1 секунду...',
  onboardCredentialsSentNotice: 'Данные учетной записи администратора отправлены в LINE и на электронную почту.',
  onboardCloseNowButton: 'Закрыть окно сейчас',
  onboardSectionHotel: 'Информация об объекте / отеле',
  onboardSectionAdmin: 'Информация об администраторе',
  onboardRequiredFieldsError: 'Пожалуйста, заполните все обязательные поля (*)',
  onboardMaxRoomsError: 'Free Tier ограничен 25 номерами. Для большего количества выберите Standard или Enterprise.',
  onboardTermsLabel: 'Я прочитал(а) и принимаю Условия использования Revio Free Tier и Политику конфиденциальности',
  onboardTermsReadLink: 'Читать полные условия и положения',
  onboardTermsModalTitle: 'Условия и положения Revio Free Tier',
  onboardTermsModalClose: 'Закрыть',
  onboardTermsModalAccept: 'Я понимаю и принимаю',
  onboardTermsRequiredError: 'Пожалуйста, примите условия и положения перед отправкой',
  onboardTermsSummaryScope: 'Объем: До 25 номеров / 10 столиков с функциями AI и подключением LINE OA',
  onboardTermsSummaryInactivity: 'Активность: При отсутствии активности более 30 дней место может быть передано следующему в очереди',
  onboardTermsSummaryNoFee: '100% бесплатно: Без скрытых платежей, без привязки банковской карты',
  onboardTermsSummaryDataPrivacy: 'Право собственности на данные: Все данные клиентов и бронирований принадлежат вам',
  onboardTermsSummaryAsIs: 'Предоставление сервиса: Сервис предоставляется «как есть» без корпоративного SLA',
};


// Japanese translations
const ja: Translations = {
  appName: '客室予約',
  loading: '読み込み中...',
  error: 'エラー',
  retry: '再試行',
  back: '戻る',
  next: '次へ',
  confirm: '確認',
  cancel: 'キャンセル',
  close: '閉じる',
  done: '完了',
  selectLanguage: '言語を選択',
  backToTop: 'トップへ戻る',

  searchTitle: '検索と予約',
  checkIn: 'チェックイン',
  checkOut: 'チェックアウト',
  adults: '大人',
  children: '子供',
  rooms: '部屋数',
  searchButton: '空室を検索',
  viewAvailabilityCalendar: '空室カレンダーを見る',
  availabilityCalendar: '空室カレンダー',
  available: '空室あり',
  notAvailable: '満室',
  allRooms: 'すべての客室',
  loadingCalendar: 'カレンダー読み込み中...',
  prevMonth: '◀ 前月',
  nextMonth: '翌月 ▶',

  roomsTitle: '客室の選択',
  perNight: '/泊',
  totalFor: '合計',
  inclTaxService: '（税・サービス料込）',
  nights: '泊',
  selectRoom: 'この客室を選択',
  noRoomsAvailable: '選択された日程に空室がありません',
  changeSearch: '日程を変更',
  amenities: '設備・アメニティ',
  cancellationPolicy: 'キャンセルポリシー',
  mealPlan: '食事プラン',

  guestInfoTitle: 'ご宿泊者情報',
  spaGuestInfoTitle: 'お客様情報（スパ）',
  diningGuestInfoTitle: 'ご予約者情報（レストラン）',
  registrantInfoTitle: '受講者・登録者情報',
  requesterInfoTitle: '申請者・連絡先情報',
  registrantName: '受講者氏名',
  requesterName: '申請者氏名',
  seminarCourse: '講習・セミナーテーマ',
  speaker: '講師・講演者',
  cpdHours: 'CPD 単位',
  confirmRegistration: '受講申込を確定する',
  registrationSuccess: '受講申込が完了しました！',
  firstName: '名（First Name）',
  lastName: '姓（Last Name）',
  email: 'メールアドレス',
  phone: '電話番号',
  specialRequests: '特別リクエスト（任意）',
  guestTitle: '敬称',
  titleMr: 'Mr.',
  titleMrs: 'Mrs.',
  titleMs: 'Ms.',
  titleMiss: 'Miss',
  titleDr: 'Dr.',
  selectTitle: '-- 敬称を選択 --',
  nationality: '国籍',
  nationalityPlaceholder: '例: 日本、タイ',
  idType: '身分証明書の種類',
  idTypePassport: 'パスポート',
  idTypeNationalId: 'マイナンバー / 国民ID',
  idTypeDrivingLicense: '運転免許証',
  idNumber: '証明書番号',
  idNumberPlaceholder: '番号を入力してください',
  selectIdType: '-- 身分証明書を選択 --',

  confirmTitle: '予約内容の確認',
  bookingSummary: '予約概要',
  roomType: '客室タイプ',
  guestName: '宿泊者氏名',
  totalAmount: '合計金額',
  confirmBooking: '予約を確定する',
  spaDetailsTitle: 'スパ詳細',
  spaTreatment: 'トリートメント',
  spaOutlet: 'スパ店舗',
  spaRate: '料金',
  diningDetailsTitle: 'ダイニング予約詳細',
  diningOutlet: 'レストラン',
  seatingArea: '座席エリア',
  serviceDate: '日付',
  timeSlot: '時間帯',
  partySize: 'ご利用人数',
  guestsCount: 'ご利用人数',
  minutesUnit: '分',
  personUnit: '名',
  roomPrefix: '部屋',
  roomNumber: '部屋番号',
  freeReservation: '無料予約',

  successTitle: '予約完了！',
  bookingConfirmed: 'ご予約が確定いたしました',
  confirmationNumber: '予約番号',
  checkInTime: 'チェックイン: 14:00',
  checkOutTime: 'チェックアウト: 12:00',
  viewInChat: 'LINEチャットで確認',
  confirmationSentToChat: '予約確認をLINEチャットに送信しました...',
  returnToChatWeb: 'ご予約が確定しました。下のボタンをタップしてチャットにお戻りください。',
  returnToWhatsAppChat: 'ご予約が確定しました。下のボタンをタップしてWhatsAppにお戻りください。',
  returnToWhatsAppBtn: 'WhatsAppに戻る',
  returnToWeChatChat: 'ご予約が確定しました。下のボタンをタップしてWeChatにお戻りください。',
  returnToWeChatBtn: 'WeChatに戻る',
  closeWindowBtn: 'このウィンドウを閉じる',
  returnToServicesBtn: 'ホーム／サービス一覧に戻る',

  errorGeneric: 'エラーが発生しました。もう一度お試しください。',
  errorNetwork: '接続できません。インターネット環境をご確認ください。',
  errorNoRooms: '指定された日程では空室がございません。',
  errorBookingFailed: '予約処理に失敗しました。もう一度お試しください。',

  required: '必須項目です',
  invalidEmail: '無効なメールアドレスです',
  invalidPhone: '無効な電話番号です',
  selectDates: '日程を選択してください',

  home: 'ホーム',
  stepSearch: '検索',
  stepRoom: '客室',
  stepGuest: '情報入力',
  stepConfirm: '確認',

  payAtHotel: '現地ホテル決済',

  // My Bookings
  myBookings: '予約確認',
  bookings: '件の予約',
  noBookings: '予約履歴がありません',
  noBookingsDesc: '現在ご予約中のプランはございません',
  startBooking: '予約を開始する',
  statusConfirmed: '予約確定',
  statusCancelled: 'キャンセル済',
  statusCompleted: '宿泊完了',
  statusPending: '確認中',

  capacityExceeded: '定員超過',
  capacityExceededMessage: '宿泊人数が客室の定員を超えています。追加料金で継続されますか？',
  extraAdults: '追加大人',
  extraChildren: '追加子供',
  person: '名',
  personPerNight: '名/泊',
  totalExtraCharge: '追加料金合計',
  extraCharges: '追加料金',
  roomRate: '客室料金',
  roomRateBeforeTax: '客室料金（税抜）',
  serviceRateBeforeTax: 'サービス料金（税抜）',
  serviceCharge10: 'サービス料 10%',
  vat7: '付加価値税 7%',
  ratePlan: '料金プラン',
  selectRatePlan: '料金プランを選択',
  hideRates: '料金を非表示',
  guests: '名',
  refundPolicy: '返金規定',
  nonRefundablePolicy: '返金不可',
  refundablePolicy: '無料キャンセル可能',
  autoFilledFromPrevious: '前回の予約情報から自動入力されました',
  editingBooking: '予約内容の変更',
  confirmEditBooking: '予約内容の変更を確定しますか？',
  confirmCreateBooking: 'この予約を確定しますか？',

  // Room Types Browse Page
  roomTypesTitle: '客室一覧',
  startingFrom: '最安',
  checkAvailability: '空室状況を確認',
  ourRooms: '客室のご案内',
  excludingTaxes: '（税・サービス料別）',

  // Payment
  paymentTitle: 'お支払い',
  payWithCard: 'クレジットカード / デビットカード',
  payWithPromptPay: 'PromptPay で支払う',
  processingPayment: '決済処理中...',
  paymentSuccessful: 'お支払いが完了しました！',
  paymentFailed: '決済に失敗しました',
  paymentError: '決済処理中にエラーが発生しました。再度お試しください。',
  retryPayment: 'もう一度支払う',
  skipPayment: '現地ホテルで支払う',
  payNow: '今すぐ支払う',
  paymentWarningBanner: '予約は確定しています。決済を完了するか「現地ホテルで支払う」を選択してください',
  paymentNoGoBack: '戻ることはできません',
  paymentBackWarning: '予約は確定済みです\n今すぐ支払うか\n「現地ホテルで支払う」を選択してください',
  statusPaid: 'オンライン決済完了',

  // Booking Management
  editBooking: '予約変更',
  editModeBanner: '予約変更モード',
  cancelEdit: '変更をキャンセル',
  cancelBooking: '予約をキャンセル',
  cannotModifyBooking: 'チェックインまで3日を切っているため変更・キャンセルできません。ホテルへ直接お問い合わせください。',
  saveChanges: '変更を保存',
  confirmCancelBooking: '本当にこの予約をキャンセルしますか？',
  keepBooking: '予約をそのままにする',
  confirmCancel: 'キャンセルを確定する',
  bookingUpdatedSuccess: '予約内容を更新しました',
  bookingCancelledSuccess: '予約をキャンセルしました',
  errorLoadingBooking: '予約情報の読み込みに失敗しました',
  errorUpdatingBooking: '予約の更新に失敗しました',
  errorCancellingBooking: '予約のキャンセルに失敗しました',
  pleaseFillAllFields: '必須項目をすべて入力してください',
  specialRequestsPlaceholder: '例: 高層階希望、エキストラベッド追加など',
  cancellationPolicy3Days: 'チェックインの3日前までにキャンセルが必要です',
  bookingWillBeCancelled: '予約はキャンセルされ、元に戻すことはできません',
  actionCannotBeUndone: 'この操作は取り消せません！',
  saving: '保存中',
  checkOutMustBeAfterCheckIn: 'チェックアウト日はチェックイン日より後の日付を指定してください',
  cannotModifyLessThan3Days: 'チェックインまで3日未満のため変更できません。ホテルへ直接お問い合わせください。',
  roomNotAvailable: '指定の日程は満室です。別の日程をお選びください',
  bookingNotFound: '予約が見つかりません',
  connectionError: '通信エラーが発生しました。電波環境を確認のうえ再試行してください',
  nonRefundable: '⛔ 返金不可',
  nonRefundableWarning: 'このプランは返金不可プランです。キャンセル・変更はできず、オンラインでの事前決済が必要です。',
  nonRefundableCannotModify: '返金不可プランは変更・キャンセルできません。ホテルへ直接お問い合わせください。',
  paidBookingCannotModify: 'この予約はお支払い済みです。変更・キャンセルはホテルへ直接お問い合わせください。',

  // Room Service & Maintenance
  roomServiceTitle: 'ルームケア＆メンテナンス',
  roomServiceSubtitle: 'アメニティのご要望やお部屋の設備修繕の依頼',
  tabHousekeeping: '🧹 ハウスキーピング',
  tabMaintenance: '🛠️ 設備修繕',
  tabMyRequests: '📋 依頼履歴',
  roomNumberLabel: '部屋番号 (Room No.)',
  roomNumberPlaceholder: '例: 302 または Villa 5',
  guestNameLabel: 'お名前 (Guest Name)',
  guestPhoneLabel: '電話番号 (Phone No.)',
  selectItemsTitle: '必要なアイテムを選択（タップして選択）',
  customNotesLabel: '備考 / その他のご要望',
  customNotesPlaceholder: '例: 羽根枕希望、追加の毛布、午後6時にお届け希望',
  submitHousekeepingBtn: 'ハウスキーピングに依頼する',
  selectIssueTitle: '不具合の種類を選択（タップして選択）',
  issueDescriptionLabel: '不具合・故障の状況',
  issueDescriptionPlaceholder: '例: エアコンから水漏れ、お湯が出ない、電球が切れている',
  submitMaintenanceBtn: '修繕依頼を送信する',
  requestSuccessTitle: 'リクエストを送信しました！',
  requestSuccessDesc: 'スタッフがリクエストを受信しました。まもなく対応いたします。',
  noActiveRequests: '現在進行中のリクエストはありません。',
  statusReqPending: '受付待ち',
  statusReqInProgress: '対応中',
  statusReqCompleted: '完了',
  requestReceivedBanner: 'スタッフがリクエストを受付けました',
  newRequestBtn: '+ 新しいリクエスト',
  roomNumberRequired: 'お部屋番号を入力してください',
  selectAtLeastOneItem: 'アイテムを1つ以上選択するか、備考にご記入ください',
  describeIssueRequired: 'カテゴリーを選択するか、症状をご記入ください',
  latestRequestsHeader: '最近のリクエスト',
  refreshStatusBtn: '🔄 更新',
  waitingStaffAccept: 'スタッフ対応待ち',
  teamHousekeeping: 'ハウスキーピング',
  teamMaintenance: 'メンテナンス',
  cancelRequestBtn: '🗑️ キャンセル',
  tapQtyHint: '(+/- をタップして数量を変更)',
  submittingRequest: 'リクエスト送信中...',
  submittingMaintenance: '報告送信中...',
  confirmCancelRequest: 'このリクエストをキャンセルしますか？',
  cannotCancelRequest: 'このリクエストはキャンセルできません',
  errorCancelRequest: 'リクエストのキャンセルに失敗しました: ',
  submitRequestFailed: 'リクエストの送信に失敗しました。再試行してください。',
  submitMaintenanceFailed: '報告の送信に失敗しました。再試行してください。',
  maxItemLimit: '1回のリクエストにつき最大 {max} 個まで',
  maxLimitReached: 'リクエスト上限（{max} 個）に達しました',
  // Restaurant Menu & Ordering
  menuOrderMode: '📋 注文する',
  menuReserveMode: '🪑 予約する',
  filterVeg: '🌿 ベジタリアン',
  filterSpicy: '🌶 辛口',
  filterGf: '🌾 グルテンフリー',
  addToCart: '追加',
  viewCart: '注文を見る',
  cartEmpty: 'カートが空です',
  deliverToRoom: '🛏️ お部屋へデリバリー',
  dineIn: '🍽️ 店内でお食事',
  chargeToRoom: '🧾 客室付け',
  payAtTable: '💳 テーブルでお支払い',
  orderSubtotal: '小計',
  orderServiceCharge: 'サービス料 (10%)',
  orderGrandTotal: '合計',
  placeOrder: '注文を確定',
  orderReceived: '✅ ご注文を受け付けました！',
  orderEta: '⏱ 所要時間の目安 約25–35分',
  soldOut: '売り切れ',
  yourOrder: 'ご注文内容',
  orderDeliveryOption: 'お届け方法',
  orderPaymentMethod: 'お支払い方法',
  addMoreItems: 'さらに追加',
  menuLoading: 'メニューを読み込み中...',
  menuEmptyTitle: 'メニューがありません',
  menuEmptySubtitle: 'このレストランにはまだメニューが登録されていません。',
  orderHistoryTitle: 'ご注文履歴',
  orderMoreBtn: '🛒 追加注文する',
  viewOrderHistoryBtn: '📋 注文履歴を確認',
  roomNumberInput: 'お部屋番号 (例 204)',
  tableNumberInput: 'テーブル番号 (例 T-05)',
  tableNumberRequired: 'テーブル番号をご入力ください',
  statusOrderReceived: '受付完了',
  statusOrderPreparing: '調理中',
  statusOrderReady: '準備完了',
  statusOrderOnTheWay: 'お届け中',
  statusOrderDelivered: 'お届け完了',
  noOrderHistory: 'ご注文履歴はありません',
  orderTotal: '合計',
  refreshBtn: '更新',
  roomBadge: '部屋',
  tableBadge: 'テーブル',
  activeOrderTracking: '注文を追跡',
  activeOrderPreparing: '調理中です...',
  activeOrderReady: '料理の準備ができました',
  activeOrderOnTheWay: 'お届け中です...',

  // Generic Multi-Industry Services
  serviceUnavailable: '現在このサービスはご利用いただけません',
  selectProvider: '担当者 / 専門スタッフを選択',
  selectLocation: '店舗 / 施設を選択',
  selectTimeSlot: '時間枠を選択',
  noSlots: '選択された日付に空きがありません',
  requestSubmitted: 'リクエストを承りました',
  quotationPending: '見積もり依頼を確認中です',
  comingSoon: '近日公開',
  serviceComingSoonDesc: '現在このサービスの準備を進めております',
  termItem: 'サービス項目',
  termProvider: '担当者',
  termLocation: '店舗・会場',
  termCustomer: 'お客様',

  // Onboarding Free Tier Application
  onboardTitle: 'Revio 無料プラン (Free Tier) のお申し込み',
  onboardSubtitle: '客室数25室以下のブティックホテル・リゾート向け 永久無料',
  onboardQuotaActiveBadge: '即時利用可能枠: 残り {slots} / {max} 施設',
  onboardQuotaFullBadge: '定員到達 (50/50) — キャンセル待ち受付中',
  onboardHotelNameLabel: '施設・ホテル名',
  onboardHotelNamePlaceholder: '例: バアンスアン・リゾート',
  onboardTenantCodeLabel: 'システムID (英小文字・数字)',
  onboardTenantCodePlaceholder: '例: baansuan-resort',
  onboardContactPersonLabel: '担当者氏名',
  onboardContactPersonPlaceholder: '例: 山田 太郎',
  onboardContactPhoneLabel: '電話番号',
  onboardContactPhonePlaceholder: '例: +66 81 234 5678',
  onboardContactEmailLabel: '通知用メールアドレス',
  onboardContactEmailPlaceholder: '例: hotel@example.com',
  onboardRoomCountLabel: '総客室数',
  onboardRoomCountHelp: '無料プランは最大25室までとなります',
  onboardProvinceLabel: '所在地（県・都市）',
  onboardProvincePlaceholder: '例: チェンマイ、プーケット、バンコク',
  onboardSelectProvincePlaceholder: '-- 都道府県を選択 --',
  onboardProvinceOther: 'その他 / 海外',
  onboardSubmitButton: '無料プランを申し込む',
  onboardSubmitting: '送信中...',
  onboardSuccessTitle: 'お申し込みが承認されました！',
  onboardPendingTitle: 'お申し込みを受付いたしました（承認待ち）',
  onboardPendingNotice: 'お申し込みを受領いたしました。現在管理者による確認中です。承認が完了次第、ログイン情報がメールおよびLINEに送信されます。',
  onboardWaitingTitle: 'キャンセル待ちに登録されました',
  onboardQueueNumberLabel: 'ウェイティング番号',
  onboardFeatureStayTitle: 'PMS客室予約システム (最大25室)',
  onboardFeatureDiningTitle: '飲食注文・ルームサービス (10席, 150件/月)',
  onboardFeatureSpaTitle: 'スパ・マッサージ予約 (2台, 30件/月)',
  onboardFeatureMiceTitle: '宴会・会議見積もりRFP (15件/月)',
  onboardFeatureAiTitle: 'AIコンシェルジュ (100通/月)',
  onboardClosingWindow: '1秒後にウィンドウを閉じてLINEに戻ります...',
  onboardCredentialsSentNotice: '管理者アカウント情報（ID/パスワード）をLINEおよびメールに送信しました。',
  onboardCloseNowButton: '今すぐ閉じる',
  onboardSectionHotel: '施設・ホテル情報',
  onboardSectionAdmin: '管理者情報',
  onboardRequiredFieldsError: '必須項目 (*) をすべて入力してください',
  onboardMaxRoomsError: '無料プランは最大25室までです。それ以上の場合はスタンダードまたはエンタープライズをご選択ください。',
  onboardTermsLabel: 'Revio 無料プラン利用規約およびプライバシーポリシーを読み、同意します',
  onboardTermsReadLink: '利用規約の全文を読む',
  onboardTermsModalTitle: 'Revio 無料プラン利用規約と条件',
  onboardTermsModalClose: '閉じる',
  onboardTermsModalAccept: '理解して同意する',
  onboardTermsRequiredError: '送信する前に利用規約に同意してください',
  onboardTermsSummaryScope: '対象範囲: 最大25室 / 10テーブル、AI機能およびLINE OA連携を含む無料利用',
  onboardTermsSummaryInactivity: '非アクティブ規定: 30日間利用がない場合、ウェイティングリストの事業者に枠が再割り当てされる場合があります',
  onboardTermsSummaryNoFee: '完全無料: 隠れた費用なし、クレジットカード登録不要、後払いの請求もありません',
  onboardTermsSummaryDataPrivacy: 'データの所有権: 顧客情報および予約データはすべてお客様に帰属します',
  onboardTermsSummaryAsIs: '提供条件: エンタープライズ向けSLA保証のない「現状有姿 (As-Is)」での提供となります',
};


// Korean translations
const ko: Translations = {
  appName: '객실 예약',
  loading: '로딩 중...',
  error: '오류',
  retry: '다시 시도',
  back: '뒤로',
  next: '다음',
  confirm: '확인',
  cancel: '취소',
  close: '닫기',
  done: '완료',
  selectLanguage: '언어 선택',
  backToTop: '맨 위로',

  searchTitle: '검색 및 예약',
  checkIn: '체크인',
  checkOut: '체크아웃',
  adults: '성인',
  children: '어린이',
  rooms: '객실 수',
  searchButton: '객실 검색',
  viewAvailabilityCalendar: '예약 가능 일정 보기',
  availabilityCalendar: '예약 가능 일정 달력',
  available: '예약 가능',
  notAvailable: '예약 마감',
  allRooms: '전체 객실',
  loadingCalendar: '달력 불러오는 중...',
  prevMonth: '◀ 이전달',
  nextMonth: '다음달 ▶',

  roomsTitle: '객실 선택',
  perNight: '/박',
  totalFor: '총 요금',
  inclTaxService: '(세금 및 봉사료 포함)',
  nights: '박',
  selectRoom: '이 객실 선택',
  noRoomsAvailable: '선택하신 날짜에 예약 가능한 객실이 없습니다',
  changeSearch: '날짜 변경',
  amenities: '편의시설',
  cancellationPolicy: '취소 규정',
  mealPlan: '식사 플랜',

  guestInfoTitle: '투숙객 정보',
  spaGuestInfoTitle: '이용자 정보 (스파)',
  diningGuestInfoTitle: '예약자 정보 (레스토랑)',
  registrantInfoTitle: '신청자 / 참가자 정보',
  requesterInfoTitle: '신청인 / 연락처 정보',
  registrantName: '참가자 성명',
  requesterName: '신청인 성명',
  seminarCourse: '과정 / 세미나 주제',
  speaker: '강사 / 발표자',
  cpdHours: 'CPD 시간',
  confirmRegistration: '참가 신청 확정',
  registrationSuccess: '참가 신청이 완료되었습니다!',
  firstName: '이름 (First Name)',
  lastName: '성 (Last Name)',
  email: '이메일 주소',
  phone: '전화번호',
  specialRequests: '특별 요청사항 (선택사항)',
  guestTitle: '호칭',
  titleMr: 'Mr.',
  titleMrs: 'Mrs.',
  titleMs: 'Ms.',
  titleMiss: 'Miss',
  titleDr: 'Dr.',
  selectTitle: '-- 호칭 선택 --',
  nationality: '국적',
  nationalityPlaceholder: '예: 대한민국, 태국',
  idType: '신분증 종류',
  idTypePassport: '여권',
  idTypeNationalId: '주민등록증',
  idTypeDrivingLicense: '운전면허증',
  idNumber: '신분증 번호',
  idNumberPlaceholder: '신분증 번호를 입력하세요',
  selectIdType: '-- 신분증 종류 선택 --',

  confirmTitle: '예약 확인',
  bookingSummary: '예약 요약',
  roomType: '객실 타입',
  guestName: '투숙객 성명',
  totalAmount: '총 결제금액',
  confirmBooking: '예약 확정하기',
  spaDetailsTitle: '스파 상세정보',
  spaTreatment: '트리트먼트',
  spaOutlet: '스파 지점',
  spaRate: '요금',
  diningDetailsTitle: '레스토랑 예약 상세',
  diningOutlet: '레스토랑',
  seatingArea: '좌석 구역',
  serviceDate: '날짜',
  timeSlot: '시간대',
  partySize: '예약 인원',
  guestsCount: '인원수',
  minutesUnit: '분',
  personUnit: '명',
  roomPrefix: '객실',
  roomNumber: '객실 번호',
  freeReservation: '무료 예약',

  successTitle: '예약 성공!',
  bookingConfirmed: '고객님의 예약이 성공적으로 확정되었습니다',
  confirmationNumber: '예약 번호',
  checkInTime: '체크인: 14:00',
  checkOutTime: '체크아웃: 12:00',
  viewInChat: 'LINE 채팅에서 보기',
  confirmationSentToChat: '예약 확인서가 LINE 채팅으로 전송되었습니다...',
  returnToChatWeb: '예약이 확정되었습니다. 아래 버튼을 눌러 창을 닫고 채팅으로 돌아가세요.',
  returnToWhatsAppChat: '예약이 확정되었습니다. 아래 버튼을 눌러 WhatsApp으로 돌아가세요.',
  returnToWhatsAppBtn: 'WhatsApp으로 돌아가기',
  returnToWeChatChat: '예약이 확정되었습니다. 아래 버튼을 눌러 WeChat으로 돌아가세요.',
  returnToWeChatBtn: 'WeChat으로 돌아가기',
  closeWindowBtn: '이 창 닫기',
  returnToServicesBtn: '홈 / 서비스 목록으로 돌아가기',

  errorGeneric: '오류가 발생했습니다. 다시 시도해 주세요.',
  errorNetwork: '네트워크 연결에 실패했습니다. 인터넷 환경을 확인해 주세요.',
  errorNoRooms: '선택하신 날짜에 예약 가능한 객실이 없습니다.',
  errorBookingFailed: '예약 처리에 실패했습니다. 다시 시도해 주세요.',

  required: '필수 입력 항목입니다',
  invalidEmail: '유효하지 않은 이메일 주소입니다',
  invalidPhone: '유효하지 않은 전화번호입니다',
  selectDates: '날짜를 선택해 주세요',

  home: '홈',
  stepSearch: '검색',
  stepRoom: '객실',
  stepGuest: '정보',
  stepConfirm: '확인',

  payAtHotel: '호텔 현장 결제',

  // My Bookings
  myBookings: '내 예약',
  bookings: '건의 예약',
  noBookings: '예약 내역 없음',
  noBookingsDesc: '현재 진행 중인 예약 내역이 없습니다',
  startBooking: '예약 시작하기',
  statusConfirmed: '확정됨',
  statusCancelled: '취소됨',
  statusCompleted: '이용완료',
  statusPending: '대기 중',

  capacityExceeded: '최대 수용 인원 초과',
  capacityExceededMessage: '투숙 인원이 객실 기준 인원을 초과했습니다. 추가 요금을 지불하고 진행하시겠습니까?',
  extraAdults: '추가 성인',
  extraChildren: '추가 아동',
  person: '명',
  personPerNight: '인/박',
  totalExtraCharge: '총 추가 요금',
  extraCharges: '추가 요금',
  roomRate: '객실 기본 요금',
  roomRateBeforeTax: '객실 요금 (세전)',
  serviceRateBeforeTax: '봉사료 (세전)',
  serviceCharge10: '봉사료 10%',
  vat7: '부가가치세 7%',
  ratePlan: '요금 플랜',
  selectRatePlan: '요금 플랜 선택',
  hideRates: '요금 숨기기',
  guests: '명',
  refundPolicy: '환불 규정',
  nonRefundablePolicy: '환불 불가',
  refundablePolicy: '무료 취소 가능',
  autoFilledFromPrevious: '이전 예약 정보를 바탕으로 자동 입력되었습니다',
  editingBooking: '예약 수정',
  confirmEditBooking: '예약 수정을 확정하시겠습니까?',
  confirmCreateBooking: '이 예약을 확정하시겠습니까?',

  // Room Types Browse Page
  roomTypesTitle: '객실 타입',
  startingFrom: '최저',
  checkAvailability: '예약 가능 여부 확인',
  ourRooms: '객실 안내',
  excludingTaxes: '(세금 및 봉사료 별도)',

  // Payment
  paymentTitle: '결제',
  payWithCard: '신용/체크카드 결제',
  payWithPromptPay: 'PromptPay 결제',
  processingPayment: '결제 진행 중...',
  paymentSuccessful: '결제가 완료되었습니다!',
  paymentFailed: '결제 실패',
  paymentError: '결제 중 오류가 발생했습니다. 다시 시도해 주세요.',
  retryPayment: '다시 결제하기',
  skipPayment: '호텔에서 직접 결제',
  payNow: '지금 결제',
  paymentWarningBanner: '예약이 확정되었습니다. 결제를 완료하시거나 「호텔 현장 결제」를 선택해 주세요',
  paymentNoGoBack: '이전 페이지로 돌아갈 수 없습니다',
  paymentBackWarning: '예약이 확정되었습니다\n지금 결제하시거나\n「호텔 현장 결제」를 선택하세요',
  statusPaid: '온라인 결제 완료',

  // Booking Management
  editBooking: '예약 변경',
  editModeBanner: '예약 변경 모드',
  cancelEdit: '변경 취소',
  cancelBooking: '예약 취소',
  cannotModifyBooking: '체크인까지 3일 미만 남은 경우 변경/취소가 불가합니다. 호텔로 직접 문의해 주세요.',
  saveChanges: '변경사항 저장',
  confirmCancelBooking: '정말 이 예약을 취소하시겠습니까?',
  keepBooking: '예약 유지',
  confirmCancel: '취소 확정',
  bookingUpdatedSuccess: '예약이 성공적으로 업데이트되었습니다',
  bookingCancelledSuccess: '예약이 성공적으로 취소되었습니다',
  errorLoadingBooking: '예약 정보를 불러오지 못했습니다',
  errorUpdatingBooking: '예약 변경에 실패했습니다',
  errorCancellingBooking: '예약 취소에 실패했습니다',
  pleaseFillAllFields: '모든 필수 항목을 입력해 주세요',
  specialRequestsPlaceholder: '예: 고층 배정 희망, 엑스트라 베드 추가 등',
  cancellationPolicy3Days: '체크인 최소 3일 전까지 취소하셔야 합니다',
  bookingWillBeCancelled: '예약이 취소되며 복구할 수 없습니다',
  actionCannotBeUndone: '이 작업은 되돌릴 수 없습니다!',
  saving: '저장 중',
  checkOutMustBeAfterCheckIn: '체크아웃 날짜는 체크인 날짜 이후여야 합니다',
  cannotModifyLessThan3Days: '체크인까지 3일 미만 남아 변경이 불가합니다. 호텔로 직접 문의해 주세요.',
  roomNotAvailable: '선택하신 날짜에 예약 가능한 객실이 없습니다. 다른 날짜를 선택해 주세요',
  bookingNotFound: '예약 정보를 찾을 수 없습니다',
  connectionError: '네트워크 오류가 발생했습니다. 연결 상태를 확인 후 다시 시도해 주세요',
  nonRefundable: '⛔ 환불 불가',
  nonRefundableWarning: '이 요금은 환불 불가 상품입니다. 예약 취소가 불가하며 온라인 결제가 필요합니다.',
  nonRefundableCannotModify: '환불 불가 상품은 일정 변경 및 취소가 불가합니다. 호텔로 문의해 주세요.',
  paidBookingCannotModify: '이미 결제가 완료된 예약입니다. 변경이나 취소는 호텔에 직접 문의해 주세요.',

  // Room Service & Maintenance
  roomServiceTitle: '룸 케어 & 시설 관리',
  roomServiceSubtitle: '객실 비품 요청 및 시설 고장 신고',
  tabHousekeeping: '🧹 하우스키핑',
  tabMaintenance: '🛠️ 시설 관리',
  tabMyRequests: '📋 내 요청 내역',
  roomNumberLabel: '객실 번호 (Room No.)',
  roomNumberPlaceholder: '예: 302호 또는 Villa 5',
  guestNameLabel: '고객명 (Guest Name)',
  guestPhoneLabel: '연락처 (Phone No.)',
  selectItemsTitle: '필요한 물품 선택 (터치하여 선택)',
  customNotesLabel: '추가 요청사항 / 메모',
  customNotesPlaceholder: '예: 깃털 베개, 여분의 담요, 오후 6시 배달 희망',
  submitHousekeepingBtn: '하우스키핑 요청 보내기',
  selectIssueTitle: '고장 유형 선택 (터치하여 선택)',
  issueDescriptionLabel: '문제 증상 설명',
  issueDescriptionPlaceholder: '예: 에어컨 누수, 온수 안 나옴, 전등 고장 등',
  submitMaintenanceBtn: '시설 신고 제출하기',
  requestSuccessTitle: '요청이 성공적으로 접수되었습니다!',
  requestSuccessDesc: '담당 부서에서 요청을 확인하였으며 곧 조치해 드리겠습니다.',
  noActiveRequests: '진행 중인 룸서비스 또는 시설 요청 내역이 없습니다.',
  statusReqPending: '접수 대기',
  statusReqInProgress: '처리 중',
  statusReqCompleted: '처리 완료',
  requestReceivedBanner: '직원이 요청을 접수했습니다',
  newRequestBtn: '+ 새 요청',
  roomNumberRequired: '객실 번호를 입력해 주세요',
  selectAtLeastOneItem: '물품을 1개 이상 선택하거나 요청사항에 작성해 주세요',
  describeIssueRequired: '고장 유형을 선택하거나 증상을 입력해 주세요',
  latestRequestsHeader: '최근 요청 내역',
  refreshStatusBtn: '🔄 상태 새로고침',
  waitingStaffAccept: '직원 접수 대기 중',
  teamHousekeeping: '하우스키핑 팀',
  teamMaintenance: '시설 관리 팀',
  cancelRequestBtn: '🗑️ 취소',
  tapQtyHint: '(+/-를 탭하여 수량 조절)',
  submittingRequest: '요청 제출 중...',
  submittingMaintenance: '신고 제출 중...',
  confirmCancelRequest: '이 요청을 취소하시겠습니까?',
  cannotCancelRequest: '이 요청을 취소할 수 없습니다',
  errorCancelRequest: '요청 취소 실패: ',
  submitRequestFailed: '요청 제출에 실패했습니다. 다시 시도해주세요.',
  submitMaintenanceFailed: '신고 제출에 실패했습니다. 다시 시도해주세요.',
  maxItemLimit: '1회 요청당 최대 {max}개',
  maxLimitReached: '요청당 최대 한도({max}개)에 도달했습니다',
  // Restaurant Menu & Ordering
  menuOrderMode: '📋 주문하기',
  menuReserveMode: '🪑 예약하기',
  filterVeg: '🌿 채식',
  filterSpicy: '🌶 매운 맛',
  filterGf: '🌾 글루텐 프리',
  addToCart: '추가',
  viewCart: '주문 보기',
  cartEmpty: '장바구니가 비어 있습니다',
  deliverToRoom: '🛏️ 객실로 배달',
  dineIn: '🍽️ 매장 식사',
  chargeToRoom: '🧾 룸 차지',
  payAtTable: '💳 테이블에서 결제',
  orderSubtotal: '소계',
  orderServiceCharge: '서비스 요금 (10%)',
  orderGrandTotal: '합계',
  placeOrder: '주문하기',
  orderReceived: '✅ 주문이 접수되었습니다!',
  orderEta: '⏱ 예상 시간 ~25–35분',
  soldOut: '품절',
  yourOrder: '주문 내역',
  orderDeliveryOption: '수령 방법',
  orderPaymentMethod: '결제 방법',
  addMoreItems: '더 추가하기',
  menuLoading: '메뉴를 불러오는 중...',
  menuEmptyTitle: '등록된 메뉴가 없습니다',
  menuEmptySubtitle: '이 레스토랑에는 아직 등록된 메뉴가 없습니다.',
  orderHistoryTitle: '주문 내역',
  orderMoreBtn: '🛒 더 주문하기',
  viewOrderHistoryBtn: '📋 주문 현황 확인',
  roomNumberInput: '객실 번호 (예: 204)',
  tableNumberInput: '테이블 번호 (예: T-05)',
  tableNumberRequired: '테이블 번호를 입력해주세요',
  statusOrderReceived: '접수 완료',
  statusOrderPreparing: '준비 중',
  statusOrderReady: '준비 완료',
  statusOrderOnTheWay: '배달 중',
  statusOrderDelivered: '배달 완료',
  noOrderHistory: '주문 내역이 없습니다',
  orderTotal: '합계',
  refreshBtn: '새로고침',
  roomBadge: '객실',
  tableBadge: '테이블',
  activeOrderTracking: '주문 추적',
  activeOrderPreparing: '음식을 준비 중입니다...',
  activeOrderReady: '음식 준비가 완료되었습니다',
  activeOrderOnTheWay: '배달 중입니다...',

  // Generic Multi-Industry Services
  serviceUnavailable: '현재 이 서비스를 이용할 수 없습니다',
  selectProvider: '전문가 / 담당자 선택',
  selectLocation: '지점 / 부서 선택',
  selectTimeSlot: '시간대 선택',
  noSlots: '선택하신 날짜에 예약 가능한 시간이 없습니다',
  requestSubmitted: '요청이 정상적으로 접수되었습니다',
  quotationPending: '견적 요청이 검토 중입니다',
  comingSoon: '출시 예정',
  serviceComingSoonDesc: '현재 서비스 오픈을 준비 중입니다',
  termItem: '서비스 항목',
  termProvider: '담당자',
  termLocation: '지점/장소',
  termCustomer: '고객',

  // Onboarding Free Tier Application
  onboardTitle: 'Revio 무료 플랜 (Free Tier) 신청',
  onboardSubtitle: '25개 객실 이하의 부티크 호텔 및 리조트를 위한 평생 무료 서비스',
  onboardQuotaActiveBadge: '즉시 이용 가능 정원: {slots} / {max} 개 호텔',
  onboardQuotaFullBadge: '정원 마감 (50/50) — 대기자 명단 신청 가능',
  onboardHotelNameLabel: '호텔 / 숙소명',
  onboardHotelNamePlaceholder: '예: 반수안 리조트',
  onboardTenantCodeLabel: '시스템 코드 (영문 소문자)',
  onboardTenantCodePlaceholder: '예: baansuan-resort',
  onboardContactPersonLabel: '관리자 성명',
  onboardContactPersonPlaceholder: '예: 홍길동',
  onboardContactPhoneLabel: '연락처',
  onboardContactPhonePlaceholder: '예: +66 81 234 5678',
  onboardContactEmailLabel: '알림 이메일',
  onboardContactEmailPlaceholder: '예: hotel@example.com',
  onboardRoomCountLabel: '총 객실 수',
  onboardRoomCountHelp: '무료 플랜은 최대 25실까지 지원됩니다',
  onboardProvinceLabel: '소재지 (도시/지역)',
  onboardProvincePlaceholder: '예: 치앙마이, 푸켓, 방콕',
  onboardSelectProvincePlaceholder: '-- 지역 선택 --',
  onboardProvinceOther: '기타 / 해외',
  onboardSubmitButton: '무료 플랜 신청하기',
  onboardSubmitting: '신청서 제출 중...',
  onboardSuccessTitle: '신청이 승인되었습니다!',
  onboardPendingTitle: '신청이 접수되었습니다 (승인 대기 중)',
  onboardPendingNotice: '신청서가 정상적으로 접수되었으며 현재 관리자 검토 중입니다. 승인이 완료되면 이메일과 LINE으로 로그인 계정 정보가 발송됩니다.',
  onboardWaitingTitle: '대기자 명단에 등록되었습니다',
  onboardQueueNumberLabel: '대기 순번',
  onboardFeatureStayTitle: 'PMS 객실 예약 시스템 (최대 25실)',
  onboardFeatureDiningTitle: '식음료 주문 및 룸서비스 (10테이블, 150건/월)',
  onboardFeatureSpaTitle: '스파 및 마사지 예약 (2베드, 30건/월)',
  onboardFeatureMiceTitle: '연회 및 미팅 견적 RFP (15건/월)',
  onboardFeatureAiTitle: 'AI 컨시어지 챗봇 (100건/월)',
  onboardClosingWindow: '1초 후 창을 닫고 LINE으로 돌아갑니다...',
  onboardCredentialsSentNotice: '관리자 계정 정보(아이디/비밀번호)가 LINE과 이메일로 발송되었습니다.',
  onboardCloseNowButton: '지금 창 닫기',
  onboardSectionHotel: '숙소 / 호텔 정보',
  onboardSectionAdmin: '관리자 정보',
  onboardRequiredFieldsError: '모든 필수 항목 (*) 을 입력해 주세요',
  onboardMaxRoomsError: '무료 플랜은 최대 25실까지 지원됩니다. 그 이상은 스탠다드 또는 엔터프라이즈를 선택해 주세요.',
  onboardTermsLabel: 'Revio 무료 플랜 이용약관 및 개인정보 처리방침을 읽었으며 이에 동의합니다',
  onboardTermsReadLink: '전체 이용약관 읽기',
  onboardTermsModalTitle: 'Revio 무료 플랜 이용약관 및 조건',
  onboardTermsModalClose: '닫기',
  onboardTermsModalAccept: '이해했으며 동의합니다',
  onboardTermsRequiredError: '신청서를 제출하기 전에 이용약관에 동의해 주세요',
  onboardTermsSummaryScope: '적용 범위: 최대 25개 객실 / 10개 테이블 무료 지원, AI 기능 및 LINE OA 연동 포함',
  onboardTermsSummaryInactivity: '비활성 규정: 30일 이상 미사용 시 대기자 명단의 다른 기업에 권한이 재할당될 수 있습니다',
  onboardTermsSummaryNoFee: '100% 무료: 숨겨진 비용 없음, 신용카드 등록 불필요, 사후 청구 없음',
  onboardTermsSummaryDataPrivacy: '데이터 소유권: 고객 및 예약 정보는 전적으로 고객사에 귀속됩니다',
  onboardTermsSummaryAsIs: '서비스 제공: 기업용 SLA 보증이 포함되지 않는 "현재 상태 그대로(As-Is)" 제공됩니다',
};


// All translations
const translations: Record<Language, Translations> = { th, en, zh, ru, ja, ko };

// Current language (default Thai)
let currentLanguage: Language = 'th';

/**
 * Set the current language
 */
export function setLanguage(lang: Language): void {
  if (translations[lang]) {
    currentLanguage = lang;
  }
}

/**
 * Get the current language
 */
export function getLanguage(): Language {
  return currentLanguage;
}

// Alias for components
export const getCurrentLanguage = getLanguage;

/**
 * Get translation for a key
 */
export function t(key: keyof Translations): string {
  return translations[currentLanguage][key] || translations['en'][key] || key;
}

/**
 * Detect language from LIFF or browser
 */
export function detectLanguage(liffLanguage?: string): Language {
  // Try LIFF language first
  if (liffLanguage) {
    if (liffLanguage.startsWith('th')) return 'th';
    if (liffLanguage.startsWith('zh')) return 'zh';
    if (liffLanguage.startsWith('ru')) return 'ru';
    if (liffLanguage.startsWith('ja')) return 'ja';
    if (liffLanguage.startsWith('ko')) return 'ko';
    if (liffLanguage.startsWith('en')) return 'en';
  }

  // Try browser language
  const browserLang = navigator.language || '';
  if (browserLang.startsWith('th')) return 'th';
  if (browserLang.startsWith('zh')) return 'zh';
  if (browserLang.startsWith('ru')) return 'ru';
  if (browserLang.startsWith('ja')) return 'ja';
  if (browserLang.startsWith('ko')) return 'ko';

  // Default to Thai
  return 'th';
}

/**
 * Format a Date object to YYYY-MM-DD string in local timezone (safe from UTC shift)
 */
export function formatLocalDate(d: Date = new Date()): string {
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

const localeMap: Record<Language, string> = {
  th: 'th-TH',
  en: 'en-US',
  zh: 'zh-CN',
  ru: 'ru-RU',
  ja: 'ja-JP',
  ko: 'ko-KR',
};

/**
 * Format date for display
 */
export function formatDate(dateStr: string): string {
  if (!dateStr) return '-';
  // If dateStr is YYYY-MM-DD, parse year, month, day explicitly in local timezone to avoid UTC shift
  const match = dateStr.match(/^(\d{4})-(\d{2})-(\d{2})/);
  let date: Date;
  if (match) {
    date = new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
  } else {
    date = new Date(dateStr);
  }

  const options: Intl.DateTimeFormatOptions = {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  };

  const locale = localeMap[currentLanguage] ?? 'en-US';

  return date.toLocaleDateString(locale, options);
}

/**
 * Format currency for display
 */
export function formatCurrency(amount: number, currency: string = 'THB'): string {
  const locale = localeMap[currentLanguage] ?? 'en-US';

  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency,
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount);
}

/**
 * Format Year and Month for display according to active language
 */
export function formatYearMonth(year: number, month: number): string {
  const d = new Date(year, month - 1, 1);
  const locale = localeMap[currentLanguage] ?? 'en-US';
  return d.toLocaleDateString(locale, { month: 'long', year: 'numeric' });
}

/**
 * Get localized single-character / short weekday header labels (Sun to Sat)
 */
export function getWeekdayHeaders(): string[] {
  if (currentLanguage === 'th') {
    return ['อา', 'จ', 'อ', 'พ', 'พฤ', 'ศ', 'ส'];
  }
  if (currentLanguage === 'zh') {
    return ['日', '一', '二', '三', '四', '五', '六'];
  }
  if (currentLanguage === 'ja') {
    return ['日', '月', '火', '水', '木', '金', '土'];
  }
  if (currentLanguage === 'ko') {
    return ['일', '월', '화', '수', '목', '금', '토'];
  }
  if (currentLanguage === 'ru') {
    return ['Вс', 'Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб'];
  }
  return ['S', 'M', 'T', 'W', 'T', 'F', 'S'];
}

// =====================================================
// Room Type & Rate Plan Translations
// These are managed by the hotel/SaaS provider
// =====================================================

type LocalizedStrings = Partial<Record<Language, string>> & { th: string; en: string };

// Room Type translations by code
const roomTypeTranslations: Record<string, LocalizedStrings> = {
  STD: { th: 'ห้องสแตนดาร์ด', en: 'Standard Room', zh: '标准房', ru: 'Стандартный номер', ja: 'スタンダードルーム', ko: '스탠다드 룸' },
  SUP: { th: 'ห้องซูพีเรียร์', en: 'Superior Room', zh: '高级房', ru: 'Номер Супериор', ja: 'スーペリアルーム', ko: '슈페리어 룸' },
  DLX: { th: 'ห้องดีลักซ์', en: 'Deluxe Room', zh: '豪华房', ru: 'Номер Делюкс', ja: 'デラックスルーム', ko: '디럭스 룸' },
  STE: { th: 'ห้องสวีท', en: 'Suite', zh: '套房', ru: 'Люкс', ja: 'スイートルーム', ko: '스위트 룸' },
  RIV: { th: 'ห้องวิวแม่น้ำ', en: 'River View Room', zh: '河景房', ru: 'Номер с видом на реку', ja: 'リバービュールーム', ko: '리버 뷰 룸' },
  EXE: { th: 'ห้องเอ็กเซ็กคิวทีฟ', en: 'Executive Room', zh: '行政房', ru: 'Представительский номер', ja: 'エグゼクティブルーム', ko: '이그제큐티브 룸' },
};

// Rate Plan translations by code
const ratePlanTranslations: Record<string, LocalizedStrings> = {
  // Standard codes
  RO: { th: 'ราคาห้องพักอย่างเดียว', en: 'Room Only', zh: '仅住宿', ru: 'Только проживание', ja: '素泊まり', ko: '룸 온리' },
  BB: { th: 'รวมอาหารเช้า', en: 'Bed & Breakfast', zh: '含早餐', ru: 'С завтраком', ja: '朝食付き', ko: '조식 포함' },
  PROMO: { th: 'โปรโมชั่นพิเศษ', en: 'Special Promotion', zh: '特别促销', ru: 'Специальная акция', ja: '特別プロモーション', ko: '특별 프로모션' },
  BIZ: { th: 'แพ็คเกจธุรกิจ', en: 'Business Package', zh: '商务套餐', ru: 'Бизнес-пакет', ja: 'ビジネスパッケージ', ko: '비즈니스 패키지' },
  // PMS variant codes
  BFST: { th: 'รวมอาหารเช้า', en: 'Bed & Breakfast', zh: '含早餐', ru: 'С завтраком', ja: '朝食付き', ko: '조식 포함' },
  BB_INCL: { th: 'รวมอาหารเช้า', en: 'Bed & Breakfast', zh: '含早餐', ru: 'С завтраком', ja: '朝食付き', ko: '조식 포함' },
  BREAKFAST: { th: 'รวมอาหารเช้า', en: 'Bed & Breakfast', zh: '含早餐', ru: 'С завтраком', ja: '朝食付き', ko: '조식 포함' },
  ROOM_ONLY: { th: 'ราคาห้องพักอย่างเดียว', en: 'Room Only', zh: '仅住宿', ru: 'Только проживание', ja: '素泊まり', ko: '룸 온리' },
  HB: { th: 'รวมอาหารครึ่งวัน', en: 'Half Board', zh: '半食宿', ru: 'Полупансион', ja: 'ハーフボード（2食付）', ko: '하프 보드' },
  FB: { th: 'รวมอาหารครบวัน', en: 'Full Board', zh: '全食宿', ru: 'Полный пансион', ja: 'フルボード（3食付）', ko: '풀 보드' },
  AI: { th: 'ออล อินคลูซีฟ', en: 'All Inclusive', zh: '全包', ru: 'Все включено', ja: 'オールインクルーシブ', ko: '올 인클루시브' },
};

// Reverse lookup: Thai name -> translation (fallback for PMS-stored Thai strings)
const ratePlanThaiToTranslation: Record<string, LocalizedStrings> = {
  'รวมอาหารเช้า': { th: 'รวมอาหารเช้า', en: 'Bed & Breakfast', zh: '含早餐', ru: 'С завтраком', ja: '朝食付き', ko: '조식 포함' },
  'ราคาห้องพักอย่างเดียว': { th: 'ราคาห้องพักอย่างเดียว', en: 'Room Only', zh: '仅住宿', ru: 'Только проживание', ja: '素泊まり', ko: '룸 온리' },
  'ไม่รวมอาหารเช้า': { th: 'ไม่รวมอาหารเช้า', en: 'Room Only', zh: '仅住宿', ru: 'Только проживание', ja: '素泊まり', ko: '조식 불포함' },
  'รวมอาหารครึ่งวัน': { th: 'รวมอาหารครึ่งวัน', en: 'Half Board', zh: '半食宿', ru: 'Полупансион', ja: 'ハーフボード（2食付）', ko: '하프 보드' },
  'รวมอาหารครบวัน': { th: 'รวมอาหารครบวัน', en: 'Full Board', zh: '全食宿', ru: 'Полный пансион', ja: 'フルボード（3食付）', ko: '풀 보드' },
  'โปรโมชั่นพิเศษ': { th: 'โปรโมชั่นพิเศษ', en: 'Special Promotion', zh: '特别促销', ru: 'Специальная акция', ja: '特別プロモーション', ko: '특별 프로모션' },
  'แพ็คเกจธุรกิจ': { th: 'แพ็คเกจธุรกิจ', en: 'Business Package', zh: '商务套餐', ru: 'Бизнес-пакет', ja: 'ビジネスパッケージ', ko: '비즈니스 패키지' },
  'ออล อินคลูซีฟ': { th: 'ออล อินคลูซีฟ', en: 'All Inclusive', zh: '全包', ru: 'Все включено', ja: 'オールインクルーシブ', ko: '올 인클루시브' },
};

// Rate Plan descriptions by code
const ratePlanDescriptions: Record<string, LocalizedStrings> = {
  RO: {
    th: 'ราคาห้องพักไม่รวมอาหารเช้า',
    en: 'Room rate without breakfast',
    zh: '房价不含早餐',
    ru: 'Тариф без завтрака',
    ja: '朝食を含まない宿泊料金',
    ko: '조식이 포함되지 않은 객실 요금',
  },
  BB: {
    th: 'ราคาห้องพักรวมอาหารเช้าบุฟเฟ่ต์',
    en: 'Room rate including buffet breakfast',
    zh: '房价含自助早餐',
    ru: 'Тариф с завтраком "шведский стол"',
    ja: 'ビュッフェ朝食付きの宿泊料金',
    ko: '뷔페 조식이 포함된 객실 요금',
  },
  PROMO: {
    th: 'ราคาพิเศษ จองล่วงหน้า 7 วัน รวมอาหารเช้า',
    en: 'Special rate - book 7 days in advance, includes breakfast',
    zh: '特价 - 提前7天预订，含早餐',
    ru: 'Спецтариф — бронь за 7 дней, с завтраком',
    ja: '特別料金 - 7日前予約、朝食付き',
    ko: '특가 요금 - 7일 전 사전 예약, 조식 포함',
  },
  BIZ: {
    th: 'รวมอาหารเช้า WiFi ความเร็วสูง และบริการซักรีด',
    en: 'Includes breakfast, high-speed WiFi, and laundry service',
    zh: '含早餐、高速WiFi和洗衣服务',
    ru: 'Включает завтрак, скоростной Wi-Fi и услуги прачечной',
    ja: '朝食、高速WiFi、ランドリーサービス付き',
    ko: '조식, 초고속 WiFi, 세탁 서비스 포함',
  },
};

// Amenity translations
const amenityTranslations: Record<string, LocalizedStrings> = {
  WIFI: { th: 'WiFi ฟรี', en: 'Free WiFi', zh: '免费WiFi', ru: 'Бесплатный Wi-Fi', ja: '無料Wi-Fi', ko: '무료 Wi-Fi' },
  TV: { th: 'ทีวี', en: 'TV', zh: '电视', ru: 'Телевизор', ja: 'テレビ', ko: 'TV' },
  MINIBAR: { th: 'มินิบาร์', en: 'Minibar', zh: '迷你吧', ru: 'Мини-бар', ja: 'ミニバー', ko: '미니바' },
  SAFE: { th: 'ตู้นิรภัย', en: 'Safe', zh: '保险箱', ru: 'Сейф', ja: '金庫', ko: '안전 금고' },
  AC: { th: 'เครื่องปรับอากาศ', en: 'Air Conditioning', zh: '空调', ru: 'Кондиционер', ja: 'エアコン', ko: '에어컨' },
  HAIRDRYER: { th: 'ไดร์เป่าผม', en: 'Hair Dryer', zh: '吹风机', ru: 'Фен', ja: 'ヘアドライヤー', ko: '헤어드라이어' },
  BATHTUB: { th: 'อ่างอาบน้ำ', en: 'Bathtub', zh: '浴缸', ru: 'Ванна', ja: 'バスタブ', ko: '욕조' },
  SOFA: { th: 'โซฟา', en: 'Sofa', zh: '沙发', ru: 'Диван', ja: 'ソファ', ko: '소파' },
  JACUZZI: { th: 'จากุซซี่', en: 'Jacuzzi', zh: '按摩浴缸', ru: 'Джакузи', ja: 'ジャグジー', ko: '자쿠지' },
  LIVING_ROOM: { th: 'ห้องนั่งเล่น', en: 'Living Room', zh: '客厅', ru: 'Гостиная', ja: 'リビングルーム', ko: '거실' },
  BALCONY: { th: 'ระเบียง', en: 'Balcony', zh: '阳台', ru: 'Балкон', ja: 'バルコニー', ko: '발코니' },
  DESK: { th: 'โต๊ะทำงาน', en: 'Work Desk', zh: '办公桌', ru: 'Письменный стол', ja: 'ワークデスク', ko: '업무용 책상' },
  LOUNGE_ACCESS: { th: 'เข้า Executive Lounge', en: 'Lounge Access', zh: '行政酒廊', ru: 'Доступ в представительский лаундж', ja: 'エグゼクティブラウンジ利用', ko: '이그제큐티브 라운지 이용' },
};

// Room description translations by room type code
// roomDescriptions hardcode removed — descriptions now come from Firestore via mock-pms

/**
 * Get translated room type name
 */
export function translateRoomType(code: string, fallback: string): string {
  const translation = roomTypeTranslations[code];
  if (translation) {
    return translation[currentLanguage] || translation.en;
  }
  return fallback;
}

/**
 * Get translated rate plan name.
 * 1. Try exact code match (e.g. BB, RO, BFST)
 * 2. Reverse lookup by Thai name (PMS stores Thai strings directly)
 */
export function translateRatePlan(code: string, fallback: string): string {
  // 1. Try exact code match
  const byCode = ratePlanTranslations[(code ?? '').toUpperCase()];
  if (byCode) {
    return byCode[currentLanguage] || byCode.en;
  }
  // 2. Reverse lookup from Thai name stored in Firestore
  const byThai = ratePlanThaiToTranslation[(fallback ?? '').trim()];
  if (byThai) {
    return byThai[currentLanguage] || byThai.en;
  }
  return fallback;
}


/**
 * Get translated rate plan description
 */
export function translateRatePlanDescription(code: string, fallback?: string): string {
  if (fallback && fallback.trim() !== '') {
    return fallback;
  }
  const translation = ratePlanDescriptions[code];
  if (translation) {
    return translation[currentLanguage] || translation.en;
  }
  return fallback || '';
}

// Dynamic custom amenities map
interface CustomAmenityItem {
  icon: string;
  name: Record<string, string>;
}
let customAmenitiesMap: Record<string, CustomAmenityItem> = {};

export function setCustomAmenities(amenities: Array<{ code: string; icon: string; name: Record<string, string> }>) {
  customAmenitiesMap = {};
  if (Array.isArray(amenities)) {
    for (const item of amenities) {
      if (item && item.code) {
        customAmenitiesMap[item.code.toUpperCase()] = {
          icon: item.icon,
          name: item.name || {},
        };
      }
    }
  }
}

export function getCustomAmenityIcon(code: string): string | undefined {
  return customAmenitiesMap[code.toUpperCase()]?.icon;
}

/**
 * Get translated amenity name
 */
export function translateAmenity(code: string): string {
  const upperCode = code.toUpperCase();
  const custom = customAmenitiesMap[upperCode];
  if (custom && custom.name) {
    return custom.name[currentLanguage] || custom.name.en || code;
  }
  const translation = amenityTranslations[upperCode];
  if (translation) {
    return translation[currentLanguage] || translation.en;
  }
  return code;
}

/**
 * Get translated room description
 */
export function translateRoomDescription(_roomTypeCode: string, fallback?: string): string {
  // Always use the description from mock-pms (Firestore) — no local hardcode override
  return fallback || '';
}
